import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext.jsx';
import { 
  Heart, MessageCircle, Bookmark, 
  UserPlus, UserCheck, Building2, Users,
  Send, X, User, Clock, Copy, Facebook, Twitter,
  MessageSquare, Search, Check, Camera, Instagram,
  Trash2, Mail, Share2, Link, Share,
  Home, Settings, LogOut, Bell, MoreVertical,
  Flag, AlertTriangle, Edit, Edit2, ExternalLink,
  ThumbsUp, ThumbsDown, Repeat, Eye, EyeOff,
  ChevronLeft, ChevronRight, Smile, Shield, ShieldAlert,
  Menu, Archive
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import EmojiPicker from 'emoji-picker-react';
import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import './Feed.css';

import { toast, localizeMessage } from '../utils/toast.js';
const API_BASE_URL = `${GREENWICH_BACKEND_URL}/api`;
const SOCKET_URL = `${GREENWICH_BACKEND_URL}`;

// ==================== RESPONSIVE HOOK ====================

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth > 768 && window.innerWidth <= 1024);
  useEffect(() => {
    const handler = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth > 768 && window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return { isMobile, isTablet };
};

// ==================== TEXT LINKIFY (hashtags & mentions) ====================

const linkifyContent = (text, navigate) => {
  if (!text) return null;
  const parts = text.split(/(#[\p{L}\p{N}_]+|@[\p{L}\p{N}_.]+)/gu);
  return parts.map((part, i) => {
    if (/^#[\p{L}\p{N}_]+$/u.test(part)) {
      const tag = part.slice(1);
      return (
        <span
          key={i}
          onClick={(e) => { e.stopPropagation(); navigate(`/search?q=${encodeURIComponent(tag)}&type=posts`); }}
          style={{ color: 'var(--fg-gold-bright, #ffd873)', fontWeight: 600, cursor: 'pointer' }}
        >{part}</span>
      );
    }
    if (/^@[\p{L}\p{N}_.]+$/u.test(part)) {
      const uname = part.slice(1);
      return (
        <span
          key={i}
          onClick={(e) => { e.stopPropagation(); navigate(`/search?q=${encodeURIComponent(uname)}&type=users`); }}
          style={{ color: 'var(--fg-gold-bright, #ffd873)', fontWeight: 600, cursor: 'pointer' }}
        >{part}</span>
      );
    }
    return part;
  });
};

// ==================== EXPANDABLE TEXT COMPONENT ====================

const ExpandableText = ({ text, maxLength = 100 }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  if (!text) return null;

  if (text.length <= maxLength) {
    return <p style={{ margin: '4px 0', lineHeight: 1.6 }}>{linkifyContent(text, navigate)}</p>;
  }

  return (
    <div style={{ margin: '4px 0' }}>
      <p style={{ margin: 0, lineHeight: 1.6, display: 'inline' }}>
        {linkifyContent(expanded ? text : text.slice(0, maxLength), navigate)}
      </p>
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--fg-gold-bright, #ffd873)',
          cursor: 'pointer',
          fontWeight: 700,
          fontSize: '13px',
          padding: '0 4px',
          marginLeft: 2,
          display: 'inline',
          verticalAlign: 'baseline',
        }}
      >
        {expanded ? ` ${t('pages.feed.seeLess')}` : `... ${t('pages.feed.seeMore')}`}
      </button>
    </div>
  );
};

// ==================== STYLED X BUTTON ====================

const XBtn = ({ onClick, label = 'Remove', variant = 'default', size = 'md', style = {}, disabled = false }) => {
  const [hovered, setHovered] = useState(false);

  const sizes = {
    sm: { btn: 22, icon: 11, font: 10, pad: '0 6px', radius: 6 },
    md: { btn: 28, icon: 13, font: 11, pad: '0 8px', radius: 8 },
    lg: { btn: 36, icon: 16, font: 12, pad: '0 10px', radius: 10 },
  };
  const s = sizes[size] || sizes.md;

  const variants = {
    default:  { bg: '#f1f5f9', hbg: '#e2e8f0', color: '#475569', hcolor: '#1e293b', border: '1.5px solid #e2e8f0' },
    danger:   { bg: '#fff1f1', hbg: '#fee2e2', color: '#dc2626', hcolor: '#b91c1c', border: '1.5px solid #fecaca' },
    dark:     { bg: 'rgba(0,0,0,0.55)', hbg: 'rgba(0,0,0,0.75)', color: '#fff', hcolor: '#fff', border: 'none' },
    ghost:    { bg: 'transparent', hbg: 'rgba(239,68,68,0.1)', color: '#94a3b8', hcolor: '#dc2626', border: '1.5px solid transparent' },
    modal:    { bg: 'rgba(255,255,255,0.12)', hbg: 'rgba(255,255,255,0.22)', color: '#fff', hcolor: '#fff', border: '1.5px solid rgba(255,255,255,0.2)' },
  };
  const v = variants[variant] || variants.default;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      title={label}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        height: s.btn, minWidth: s.btn,
        padding: label ? s.pad : 0,
        borderRadius: s.radius,
        border: v.border,
        background: hovered ? v.hbg : v.bg,
        color: hovered ? v.hcolor : v.color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'all .15s',
        flexShrink: 0,
        fontFamily: 'inherit',
        fontSize: s.font,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        transform: hovered ? 'scale(1.05)' : 'scale(1)',
        ...style,
      }}
    >
      <X size={s.icon} strokeWidth={2.5} />
      {label && <span>{label}</span>}
    </button>
  );
};

class ImageModerator {
  constructor() {
    this.moderationCache = new Map();
    this.moderationInProgress = new Set();
  }

  async isImageSafe(file) {
    if (this.moderationCache.has(file.name)) {
      return this.moderationCache.get(file.name);
    }
    if (this.moderationInProgress.has(file.name)) {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.moderationCache.has(file.name)) {
            clearInterval(checkInterval);
            resolve(this.moderationCache.get(file.name));
          }
        }, 100);
      });
    }
    this.moderationInProgress.add(file.name);
    try {
      const result = await this.analyzeImage(file);
      this.moderationCache.set(file.name, result);
      this.moderationInProgress.delete(file.name);
      setTimeout(() => { this.moderationCache.delete(file.name); }, 300000);
      return result;
    } catch (error) {
      console.error('Error moderating image:', error);
      this.moderationInProgress.delete(file.name);
      return { safe: false, reason: 'Moderation failed' };
    }
  }

  async analyzeImage(file) {
    return new Promise((resolve) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 400;
          let width = img.width;
          let height = img.height;
          if (width > maxSize) { height = (height * maxSize) / width; width = maxSize; }
          if (height > maxSize) { width = (width * maxSize) / height; height = maxSize; }
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          try {
            const imageData = ctx.getImageData(0, 0, width, height);
            const result = this.analyzeImageData(imageData, width, height);
            resolve(result);
          } catch (error) {
            resolve({ safe: false, reason: 'Analysis error' });
          }
        };
        img.onerror = () => { resolve({ safe: false, reason: 'Invalid image' }); };
      };
      reader.onerror = () => { resolve({ safe: false, reason: 'Failed to read image' }); };
      reader.readAsDataURL(file);
    });
  }

  analyzeImageData(imageData, width, height) {
    const data = imageData.data;
    let skinPixels = 0, extremeRedPixels = 0, gorePixels = 0;
    const totalPixels = width * height;
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i], g = data[i + 1], b = data[i + 2];
      if (this.isSkinTone(r, g, b)) skinPixels++;
      if (r > 200 && g < 40 && b < 40) extremeRedPixels++;
      if (this.isGoreColor(r, g, b)) gorePixels++;
    }
    const skinRatio = skinPixels / totalPixels;
    const extremeRedRatio = extremeRedPixels / totalPixels;
    const goreRatio = gorePixels / totalPixels;

    if (skinRatio > 0.55) {
      return { safe: false, reason: 'Image may contain explicit 18+ content', confidence: skinRatio };
    }
    if (extremeRedRatio > 0.18) {
      return { safe: false, reason: 'Image may contain graphic violent content', confidence: extremeRedRatio };
    }
    if (skinRatio > 0.45 && goreRatio > 0.08) {
      return { safe: false, reason: 'Image may contain inappropriate content', confidence: skinRatio };
    }
    return { safe: true, reason: '' };
  }

  isSkinTone(r, g, b) {
    const max = Math.max(r, g, b), min = Math.min(r, g, b), diff = max - min;
    return (r > 60 && g > 40 && b > 20) && r > g && g > b && diff < 70 && r < 250 && g < 220 && b < 200;
  }
  isFleshTone(r, g, b) {
    const ratios = [r / (g + 1), r / (b + 1), g / (b + 1)];
    return r > 70 && g > 40 && b > 20 && ratios[0] > 0.8 && ratios[0] < 1.6 && ratios[1] > 0.9 && ratios[1] < 2.0 && Math.abs(r - g) < 50;
  }
  isBloodRed(r, g, b) { return r > 140 && g < 100 && b < 100 && r > g * 1.5 && r > b * 1.5; }
  isGoreColor(r, g, b) {
    return (r > 100 && g < 80 && b > 30 && b < 100) || (r > 150 && g > 30 && g < 100 && b > 20 && b < 70);
  }
  clearCache(imageName) { this.moderationCache.delete(imageName); }
  clearAllCache() { this.moderationCache.clear(); this.moderationInProgress.clear(); }
}

const imageModerator = new ImageModerator();

// ==================== IMAGE RESIZE UTILITY ====================

const resizeImage = (file, maxWidth = 800, maxHeight = 800, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith('image/')) { resolve(file); return; }
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        if (img.width <= maxWidth && img.height <= maxHeight) { resolve(file); return; }
        const canvas = document.createElement('canvas');
        let width = img.width, height = img.height;
        if (width > maxWidth) { height = (height * maxWidth) / width; width = maxWidth; }
        if (height > maxHeight) { width = (width * maxHeight) / height; height = maxHeight; }
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name, { type: 'image/jpeg', lastModified: Date.now() }));
        }, 'image/jpeg', quality);
      };
      img.onerror = () => { resolve(file); };
      img.src = e.target.result;
    };
    reader.onerror = () => { resolve(file); };
    reader.readAsDataURL(file);
  });
};

// ==================== API REQUEST HELPER ====================

const apiRequest = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('No authentication token found');
  const defaultHeaders = {
    'Authorization': `Bearer ${token}`,
    ...(options.body && typeof options.body === 'string' ? { 'Content-Type': 'application/json' } : {})
  };
  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: { ...defaultHeaders, ...options.headers }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }
    return response.json();
  } catch (error) {
    if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
      throw new Error('Cannot connect to server. Please check if backend is running');
    }
    throw error;
  }
};

// ==================== HELPERS ====================

const handleImageError = (e) => {
  e.target.onerror = null;
  e.target.src = '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
};

const getSafeUserId = (user) => {
  if (!user) return null;
  return user._id || user.id || null;
};

const getSafeUserName = (user) => {
  if (!user) return 'Unknown User';
  return user.organizationName || user.name || user.username || 'Unknown User';
};

const saveUserInteractions = (userId, type, data) => {
  try {
    const storageKey = `user_${userId}_interactions`;
    const existing = JSON.parse(localStorage.getItem(storageKey) || '{}');
    if (type === 'likes') existing.likes = { ...(existing.likes || {}), ...data };
    else if (type === 'saves') existing.saves = { ...(existing.saves || {}), ...data };
    localStorage.setItem(storageKey, JSON.stringify(existing));
  } catch (error) { console.error('Error saving user interactions:', error); }
};

const loadUserInteractions = (userId) => {
  try {
    const storageKey = `user_${userId}_interactions`;
    const data = JSON.parse(localStorage.getItem(storageKey) || '{}');
    return { likes: data.likes || {}, saves: data.saves || {} };
  } catch (error) { return { likes: {}, saves: {} }; }
};

// ==================== IMAGE CAROUSEL ====================

const ImageCarousel = ({ images = [], getImageUrl, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  if (!images || images.length === 0) return null;
  const goToPrev = (e) => { e?.stopPropagation(); setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1); };
  const goToNext = (e) => { e?.stopPropagation(); setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1); };
  const handleDotClick = (e, index) => { e.stopPropagation(); setCurrentIndex(index); if (onImageClick) onImageClick(index); };
  const handleImageClick = (e) => { e.stopPropagation(); if (onImageClick) onImageClick(currentIndex); };
  return (
    <div className="image-carousel-container" style={{ position: 'relative' }}
      onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
      <div className="carousel-image-wrapper" onClick={handleImageClick} style={{ cursor: 'pointer', position: 'relative' }}>
        <img src={getImageUrl(images[currentIndex])} alt={`Image ${currentIndex + 1}`} loading="lazy" decoding="async"
          style={{ width: '100%', height: 'auto', maxHeight: '250px', objectFit: 'contain', backgroundColor: '#ffffff', borderRadius: '8px' }}
          onError={handleImageError} />
      </div>
      {images.length > 1 && (
        <>
          <div className="carousel-controls" style={{ position: 'absolute', top: '50%', left: 0, right: 0, transform: 'translateY(-50%)', display: isHovered ? 'flex' : 'none', justifyContent: 'space-between', alignItems: 'center', pointerEvents: 'none', zIndex: 2 }}>
            <button className="carousel-btn" onClick={goToPrev} style={{ pointerEvents: 'auto', marginLeft: '5px' }}>{"<"}</button>
            <button className="carousel-btn" onClick={goToNext} style={{ pointerEvents: 'auto', marginRight: '5px' }}>{">"}</button>
          </div>
          <div className="carousel-dots" style={{ position: 'absolute', bottom: '20px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '12px', zIndex: 2 }}>
            {images.map((_, index) => (
              <button key={index} onClick={(e) => handleDotClick(e, index)} className={`carousel-dot ${index === currentIndex ? 'active' : ''}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ==================== MULTI-IMAGE LIGHTBOX ====================

const MultiImageLightbox = ({ images, isOpen, onClose, getImageUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  useEffect(() => {
    if (!isOpen || !images || images.length === 0) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1);
      if (e.key === 'ArrowRight') setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, images]);
  if (!isOpen || !images || images.length === 0) return null;
  return (
    <div className="image-lightbox" onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="lightbox-content" onClick={(e) => e.stopPropagation()} style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
        <img src={getImageUrl(images[currentIndex])} alt={`Image ${currentIndex + 1}`} style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: '4px' }} />
        {images.length > 1 && (
          <>
            <button className="lightbox-arrow left-arrow" onClick={() => setCurrentIndex(prev => prev === 0 ? images.length - 1 : prev - 1)} style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '50px', height: '50px', cursor: 'pointer', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={24} /></button>
            <button className="lightbox-arrow right-arrow" onClick={() => setCurrentIndex(prev => prev === images.length - 1 ? 0 : prev + 1)} style={{ position: 'absolute', right: '20px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', borderRadius: '50%', width: '50px', height: '50px', cursor: 'pointer', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={24} /></button>
          </>
        )}
      </div>
      <div style={{ position: 'absolute', top: '20px', right: '20px' }}>
        <XBtn onClick={onClose} label="Close" variant="dark" size="lg" style={{ borderRadius: '50%', width: 42, height: 42 }} />
      </div>
    </div>
  );
};

// ==================== DELETE POST MODAL (ADMIN SUPPORT) ====================

const DeletePostModal = ({ isOpen, onClose, onDelete, postId, post, currentUser }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!isOpen) return null;

  const isOwner = post?.author?._id === currentUser?._id ||
                  post?.author?._id === currentUser?.id ||
                  post?.author?.id  === currentUser?._id;
  const isAdminDeleting = (currentUser?.role === 'admin' || currentUser?.userType === 'admin') && !isOwner;
  const authorName = post?.author?.organizationName || post?.author?.name || post?.author?.username || 'this user';

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await onDelete(postId, isAdminDeleting ? reason.trim() : undefined, isAdminDeleting);
      setReason('');
      onClose();
    } catch (error) {
      console.error('Error deleting post:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="modal-content delete-modal clay-modal-enter" onClick={(e) => e.stopPropagation()}
        style={{ border: isAdminDeleting ? '1px solid rgba(239,68,68,0.3)' : undefined }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {isAdminDeleting ? (
              <ShieldAlert size={20} color="#dc2626" />
            ) : (
              <Trash2 size={20} color="#ef4444" />
            )}
            <h3 style={{ color: isAdminDeleting ? '#dc2626' : undefined }}>
              {isAdminDeleting ? `Admin: ${t("postModal.deletePost")}` : t("postModal.deletePost")}
            </h3>
          </div>
          <XBtn onClick={onClose} label={t("feed.close")} variant="default" size="md" />
        </div>
        <div className="modal-body">
          <div className="delete-warning">
            {isAdminDeleting ? (
              <ShieldAlert size={48} style={{ color: '#dc2626', marginBottom: '16px' }} />
            ) : (
              <Trash2 size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
            )}
            <h4>{isAdminDeleting ? `Delete ${authorName}'s post?` : t("postModal.areYouSure")}</h4>
            <p>
              {isAdminDeleting
                ? 'This post will be permanently deleted. The author will receive a notification.'
                : t("postModal.postWillBeDeleted")}
            </p>
          </div>
          {isAdminDeleting && (
            <div style={{ marginTop: '16px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#7a8a82', marginBottom: '6px' }}>
                Reason for deletion <span style={{ fontWeight: 400, color: '#9aaa9f' }}>(optional, shown to user)</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Violates community guidelines..."
                maxLength={300}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 12px',
                  borderRadius: '10px', border: '1px solid rgba(239,68,68,0.3)',
                  background: 'rgba(255,255,255,0.8)', fontSize: '13px',
                  lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit', outline: 'none'
                }}
              />
              <div style={{ fontSize: '11px', color: '#9aaa9f', textAlign: 'right', marginTop: '4px' }}>{reason.length}/300</div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={isDeleting}>{t("postModal.cancel")}</button>
          <button className="delete-btn" onClick={handleDelete} disabled={isDeleting}
            style={{ background: isAdminDeleting ? 'linear-gradient(135deg,#ef4444,#b91c1c)' : undefined }}>
            {isDeleting ? <div className="mini-spinner"></div> : isAdminDeleting ? t("postModal.deletePost") : t("postModal.delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== REPORT MODAL ====================

const ReportModal = ({ isOpen, onClose, onReport, type, targetId }) => {
  const { t } = useTranslation();
  const [reportReason, setReportReason] = useState('');
  const [isReporting, setIsReporting] = useState(false);
  const [selectedReason, setSelectedReason] = useState('');
  const reportReasons = [
    { id: 'spam', label: t("postModal.reasonSpam"), description: t("feed.reasonDescSpam") },
    { id: 'inappropriate', label: t("postModal.reasonInappropriate"), description: t("feed.reasonDescInappropriate") },
    { id: 'violent', label: t("feed.reasonViolent"), description: t("feed.reasonDescViolent") },
    { id: 'harassment', label: t("postModal.reasonHarassment"), description: t("feed.reasonDescHarassment") },
    { id: 'hate_speech', label: t("postModal.reasonHateSpeech"), description: t("feed.reasonDescHateSpeech") },
    { id: 'misinformation', label: t("postModal.reasonMisinformation"), description: t("feed.reasonDescMisinformation") },
    { id: 'other', label: t("postModal.reasonOther"), description: t("feed.reasonDescOther") }
  ];
  const handleReport = async () => {
    if (!selectedReason) return;
    try {
      setIsReporting(true);
      await onReport({ type, targetId, reason: selectedReason, details: reportReason });
      onClose();
    } catch (error) { console.error('Error reporting:', error); }
    finally { setIsReporting(false); }
  };
  if (!isOpen) return null;
  return (
    <div className="modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="modal-content report-modal clay-modal-enter" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <AlertTriangle size={20} />
          <h3>{type === 'post' ? t("postModal.reportPost") : t("feed.reportComment")}</h3>
          <XBtn onClick={onClose} label={t("feed.close")} variant="default" size="md" />
        </div>
        <div className="modal-body">
          <p className="report-description">{t("feed.reportSelectReasonHint", { type })}</p>
          <div className="report-reasons">
            {reportReasons.map(reason => (
              <div key={reason.id} className={`report-reason ${selectedReason === reason.id ? 'selected' : ''}`} onClick={() => setSelectedReason(reason.id)}>
                <div className="reason-radio">{selectedReason === reason.id && <div className="radio-dot"></div>}</div>
                <div className="reason-content">
                  <div className="reason-label">{reason.label}</div>
                  <div className="reason-description">{reason.description}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="report-details">
            <label htmlFor="reportDetails">{t("feed.additionalDetails")}</label>
            <textarea id="reportDetails" placeholder={t("feed.reportDetailsPlaceholder")} value={reportReason} onChange={(e) => setReportReason(e.target.value)} rows={3} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={isReporting}>{t("postModal.cancel")}</button>
          <button className="report-btn" onClick={handleReport} disabled={!selectedReason || isReporting}>
            {isReporting ? <div className="mini-spinner"></div> : t("feed.submitReport")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== POST OPTIONS (ADMIN SUPPORT) ====================

const PostOptions = ({ post, onDelete, onReport, onEdit, onArchive, currentUserId, currentUser }) => {
  const { t } = useTranslation();
  const [showOptions, setShowOptions] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const optionsRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) setShowOptions(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const isOwner = post.author?._id === currentUserId || post.author?.id === currentUserId;
  const isAdminUser = currentUser?.role === 'admin' || currentUser?.userType === 'admin';
  const canDelete = isOwner || isAdminUser;
  const canEdit = isOwner;

  return (
    <div className="post-options-container" ref={optionsRef}>
      <span className="post-options-icon" onClick={(e) => { e.stopPropagation(); setShowOptions(!showOptions); }} style={{ cursor: 'pointer' }}>
        <MoreVertical size={20} />
      </span>
      {showOptions && (
        <div className="post-options-menu">
          {canEdit && (
            <div className="post-option" onClick={(e) => { e.stopPropagation(); if (onEdit) onEdit(post); setShowOptions(false); }}>
              <Edit2 size={16} /><span>{t("feed.editPost")}</span>
            </div>
          )}
          {isOwner && (
            <div className="post-option" onClick={async (e) => {
              e.stopPropagation();
              if (archiving) return;
              setArchiving(true);
              try { await onArchive?.(post._id); } finally { setArchiving(false); }
              setShowOptions(false);
            }}>
              <Archive size={16} /><span>{archiving ? t("feed.archiving") : t("feed.archivePost")}</span>
            </div>
          )}
          {canDelete && (
            <div className="post-option delete" onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(post._id); setShowOptions(false); }}
              style={{ color: isAdminUser && !isOwner ? '#dc2626' : undefined }}>
              {isAdminUser && !isOwner ? <ShieldAlert size={16} /> : <Trash2 size={16} />}
              <span>{isAdminUser && !isOwner ? `Admin: ${t("postModal.delete")}` : t("postModal.deletePost")}</span>
            </div>
          )}
          {!isOwner && (
            <div className="post-option report" onClick={(e) => { e.stopPropagation(); if (onReport) onReport(post._id); setShowOptions(false); }}>
              <Flag size={16} /><span>{t("postModal.reportPost")}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ==================== DELETE COMMENT MODAL ====================

const DeleteCommentModal = ({ isOpen, onClose, onDelete, commentId }) => {
  const { t } = useTranslation();
  const [isDeleting, setIsDeleting] = useState(false);
  const handleDelete = async () => {
    try { setIsDeleting(true); await onDelete(commentId); onClose(); }
    catch (error) { console.error('Error deleting comment:', error); }
    finally { setIsDeleting(false); }
  };
  if (!isOpen) return null;
  return (
    <div className="modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="modal-content delete-modal clay-modal-enter" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t("postModal.deleteComment")}</h3>
          <XBtn onClick={onClose} label={t("feed.close")} variant="default" size="md" />
        </div>
        <div className="modal-body">
          <div className="delete-warning">
            <Trash2 size={48} style={{ color: '#ef4444', marginBottom: '16px' }} />
            <h4>{t("postModal.deleteThisComment")}</h4>
            <p>{t("feed.commentWillBeDeleted")}</p>
          </div>
        </div>
        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={isDeleting}>{t("postModal.cancel")}</button>
          <button className="delete-btn" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? <div className="mini-spinner"></div> : t("postModal.delete")}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== COMMENT ITEM ====================

const CommentItem = ({ comment, depth = 0, onLike, onReply, onReport, onDelete, currentUserId, postId, getImageUrl, formatDate, replyInputs, setReplyInputs, handleCommentReply, activeEmojiComment, setActiveEmojiComment, onDeleteClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showOptions, setShowOptions] = useState(false);
  const [isLiking, setIsLiking] = useState(false);
  const optionsRef = useRef(null);
  const isLiked = comment.likes?.includes(currentUserId) || false;

  const handleLikeClick = async () => {
    if (isLiking || !onLike) return;
    setIsLiking(true);
    try { await onLike(comment._id); } catch (error) { console.error('Error liking comment:', error); }
    finally { setIsLiking(false); }
  };

  const handleReplyClick = () => {
    const parentAuthorName = comment.author?.name || 'User';
    setReplyInputs({ ...replyInputs, [comment._id]: `@${parentAuthorName} ` });
    setTimeout(() => {
      const replyInput = document.querySelector(`.reply-input[data-comment-id="${comment._id}"]`);
      if (replyInput) replyInput.focus();
    }, 100);
  };

  const handleEmojiClick = (emoji, commentId) => {
    if (replyInputs[commentId] !== undefined) setReplyInputs({ ...replyInputs, [commentId]: (replyInputs[commentId] || '') + emoji });
  };

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) setShowOptions(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const maxDepth = 10;

  return (
    <div className="comment-item" style={{ marginLeft: depth > 0 ? '32px' : '0', marginTop: depth > 0 ? '8px' : '0', position: 'relative' }}>
      {depth > 0 && <div className="reply-connector" style={{ position: 'absolute', left: '-16px', top: '24px', bottom: '0', width: '2px', backgroundColor: '#e0e0e0' }}></div>}
      <div className="comment" style={{ display: 'flex', gap: '8px' }}>
        <img src={getImageUrl(comment.author?.profileImage)} alt={comment.author?.name} className="comment-avatar" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} onError={handleImageError} />
        <div className="comment-content" style={{ flex: 1 }}>
          <div className="comment-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span className="comment-author-name" style={{ fontWeight: '600', fontSize: '14px' }}>{comment.author?.name || 'Unknown'}</span>
            <span className="comment-time" style={{ fontSize: '12px', color: 'var(--ink-400)' }}>{formatDate(comment.createdAt)}</span>
          </div>
          <p className="comment-text" style={{ margin: '4px 0', fontSize: '14px', lineHeight: '1.4' }}>{linkifyContent(comment.content || '', navigate)}</p>
          <div className="comment-actions" style={{ display: 'flex', gap: '12px', marginTop: '4px' }}>
            <button className={`comment-action-btn like-btn clay-press ${isLiked ? 'liked' : ''}`} onClick={handleLikeClick} disabled={isLiking} style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 4px', cursor: 'pointer', fontSize: '12px', color: isLiked ? '#ef4444' : 'var(--ink-400)' }}>
              <Heart size={12} fill={isLiked ? '#ef4444' : 'none'} color={isLiked ? '#ef4444' : 'var(--ink-400)'} />
              <span>{comment.likes?.length || 0}</span>
            </button>
            {depth < maxDepth && (
              <button className="comment-action-btn reply-btn clay-press" onClick={handleReplyClick} style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', gap: '4px', padding: '2px 4px', cursor: 'pointer', fontSize: '12px', color: 'var(--ink-400)' }}>
                <Repeat size={12} /><span>{t("postModal.reply")}</span>
              </button>
            )}
            <div className="comment-options-container" ref={optionsRef} style={{ position: 'relative' }}>
              <button className="comment-action-btn more-btn clay-press" onClick={() => setShowOptions(!showOptions)} style={{ background: 'none', border: 'none', display: 'flex', alignItems: 'center', padding: '2px 4px', cursor: 'pointer', fontSize: '12px', color: 'var(--ink-400)' }}>
                <MoreVertical size={12} />
              </button>
              {showOptions && (
                <div className="comment-options-menu" style={{ position: 'absolute', top: '100%', right: '0', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 100, minWidth: '150px' }}>
                  <button className="comment-option" onClick={() => { onReport(comment._id); setShowOptions(false); }} style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--ink-400)' }}>
                    <Flag size={14} /><span>{t("postModal.report")}</span>
                  </button>
                  {comment.author?._id === currentUserId && (
                    <button className="comment-option delete" onClick={() => { if (onDeleteClick && comment._id) { onDeleteClick(comment._id, postId); setShowOptions(false); } }} style={{ width: '100%', padding: '8px 12px', border: 'none', background: 'none', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#ef4444' }}>
                      <Trash2 size={14} /><span>{t("postModal.delete")}</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          {replyInputs[comment._id] !== undefined && (
            <div className="reply-input-container" style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center', position: 'relative' }}>
              <div className="reply-input-wrapper" style={{ flex: 1, position: 'relative' }}>
                <input type="text" placeholder="Write a reply..." value={replyInputs[comment._id] || ''} onChange={(e) => setReplyInputs({ ...replyInputs, [comment._id]: e.target.value })} onKeyPress={(e) => e.key === 'Enter' && handleCommentReply(comment._id, postId)} className="reply-input" data-comment-id={comment._id} style={{ width: '100%', padding: '8px 12px', paddingRight: '36px', border: '1px solid #e0e0e0', borderRadius: '20px', fontSize: '13px' }} />
                <button className="emoji-toggle-btn" onClick={() => setActiveEmojiComment(activeEmojiComment === comment._id ? null : comment._id)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--ink-400)' }}>
                  <Smile size={16} />
                </button>
              </div>
              <button className="reply-submit-btn" onClick={() => handleCommentReply(comment._id, postId)} disabled={!replyInputs[comment._id]?.trim()} style={{ background: 'var(--green-700)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: replyInputs[comment._id]?.trim() ? 1 : 0.5 }}>
                <Send size={14} />
              </button>
              <XBtn
                onClick={() => setReplyInputs(prev => { const n = {...prev}; delete n[comment._id]; return n; })}
                label="Cancel"
                variant="ghost"
                size="sm"
              />
              {activeEmojiComment === comment._id && (
                <div style={{ position: 'absolute', bottom: '100%', left: '0', zIndex: 1000 }}>
                  <EmojiPicker onEmojiClick={(emojiData) => { handleEmojiClick(emojiData.emoji, comment._id); setActiveEmojiComment(null); }} disableAutoFocus={true} native={true} height={350} width={300} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== SHARE MODAL (with improved visibility) ====================

const ShareModal = ({ isOpen, onClose, post, following, followers, onSelectUser, currentUserId, isSending = false, onLinkCopied }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [showAllUsers, setShowAllUsers] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
      document.addEventListener('keydown', handleEscape);
      return () => { document.body.classList.remove('modal-open'); document.removeEventListener('keydown', handleEscape); };
    }
  }, [isOpen, onClose]);

  useEffect(() => { if (isOpen) loadAvailableUsers(); }, [isOpen]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.includes('/uploads/') || imagePath.includes('/nkarner/')) {
      const normalized = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      return `${GREENWICH_BACKEND_URL}${normalized}`;
    }
    return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
  };

  const loadAvailableUsers = async () => {
    try {
      setLoading(true);
      const safeFollowing = Array.isArray(following) ? following : [];
      const safeFollowers = Array.isArray(followers) ? followers : [];
      const mutualFollows = safeFollowing.filter(followingUser => {
        const userId = followingUser._id || followingUser.id;
        const isMutual = safeFollowers.some(follower => (follower._id || follower.id || follower.follower?._id) === userId);
        return isMutual && userId !== currentUserId;
      });
      setAvailableUsers(mutualFollows);
    } catch (error) { setAvailableUsers([]); }
    finally { setLoading(false); }
  };

  const handleShareToSocial = async (platform) => {
    if (!post || !post._id) return;
    const postUrl = `${window.location.origin}/post/${post._id}`;
    const postText = post.content ? post.content.substring(0, 100) : 'Check out this post';
    const encodedText = encodeURIComponent(postText);
    const encodedUrl = encodeURIComponent(postUrl);
    switch (platform) {
      case 'copy':
        try { await navigator.clipboard.writeText(postUrl); if (typeof onLinkCopied === 'function') onLinkCopied(); onClose(); } catch (error) { toast(t('feed.failedToCopyLink')); }
        break;
      case 'whatsapp': window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank'); onClose(); break;
      case 'facebook': window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, '_blank'); onClose(); break;
      case 'twitter': window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank'); onClose(); break;
      case 'instagram':
        try { await navigator.clipboard.writeText(`${postText}\n${postUrl}`); if (typeof onLinkCopied === 'function') onLinkCopied(); onClose(); } catch (error) { toast(t('feed.failedToCopyLink')); }
        break;
      case 'telegram': window.open(`https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, '_blank'); onClose(); break;
      default: break;
    }
  };

  const filteredUsers = availableUsers.filter(user => {
    const userData = user.user || user.follower || user.followedUser || user;
    const userName = userData.organizationName || userData.name || userData.username || '';
    return userName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleSelectUser = (user) => {
    const userData = user.user || user.follower || user.followedUser || user;
    setSelectedUser(userData);
  };

  const handleConfirm = () => { if (selectedUser && onSelectUser) onSelectUser(selectedUser, post); };

  if (!isOpen) return null;

  const displayedUsers = showAllUsers ? filteredUsers : filteredUsers.slice(0, 6);

  return (
    <div className="modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="modal-content share-modal-compact clay-modal-enter" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h3><Send size={20} style={{ marginRight: '8px' }} />{t("feed.sendAndShare")}</h3>
          <XBtn onClick={onClose} label={t("feed.close")} variant="default" size="md" />
        </div>

        {post && (
          <div style={{ margin: '0 20px 0', padding: '12px', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.1)', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            {post.images && post.images.length > 0 && (
              <img
                src={post.images[0].startsWith('http') ? post.images[0] : `${GREENWICH_BACKEND_URL}${post.images[0].startsWith('/') ? '' : '/'}${post.images[0]}`}
                alt="Post"
                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '8px', flexShrink: 0 }}
                onError={handleImageError}
              />
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--fg-gold-bright, #ffd873)', marginBottom: '4px' }}>
                {post.author?.organizationName || post.author?.name || t("messageModal.user")}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--fg-text-mid, rgba(234,243,238,0.78))', lineHeight: '1.4', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {post.content || (post.images?.length > 0 ? `📷 ${t("feed.imagePost")}` : t("feed.postBtn"))}
              </div>
            </div>
          </div>
        )}
        <div className="share-modal-body-compact" style={{ flex: 1, overflowY: 'auto', padding: '0 20px 20px' }}>
          <div className="send-section-compact">
            <div className="section-header-compact"><MessageCircle size={18} /><h4>{t("feed.sendToMutualFollows")}</h4></div>
            <div className="users-container">
              {loading ? (
                <div className="loading-state-compact"><div className="spinner-small"></div><span>{t("feed.loadingMutualFollows")}</span></div>
              ) : availableUsers.length === 0 ? (
                <div className="no-mutual-follows-compact">
                  <UserCheck size={32} style={{ color: '#ccc', marginBottom: '12px' }} />
                  <p>{t("messageModal.noMutualFollows")}</p>
                  <small>{t("messageModal.noMutualFollowsHint")}</small>
                </div>
              ) : (
                <>
                  <div className="search-row">
                    <div className="search-container"><Search size={16} /><input type="text" placeholder={t("common.searchMutualFollows")} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input-compact" /></div>
                    <div className="users-count-badge"><UserCheck size={14} /><span>{filteredUsers.length}</span></div>
                  </div>
                  <div className="users-grid-compact">
                    {displayedUsers.map((user, index) => {
                      const userData = user.user || user.follower || user.followedUser || user;
                      const userName = userData.organizationName || userData.name || userData.username || t("feed.unknown");
                      const userImage = userData.profileImage || '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
                      const isVerified = userData.isVerified || false;
                      return (
                        <div key={`user-${userData._id || index}`} className={`user-card-compact ${selectedUser?._id === userData._id ? 'selected' : ''}`} onClick={() => handleSelectUser(user)} title={userName}>
                          <div className="user-avatar-compact">
                            <img src={getImageUrl(userImage)} alt={userName} onError={handleImageError} />
                            {isVerified && userData.userType === 'organizer' && <div className="verified-badge-compact">✓</div>}
                          </div>
                          <div className="user-name-compact">{userName.length > 12 ? userName.substring(0, 12) + '...' : userName}</div>
                          {selectedUser?._id === userData._id && <div className="selection-indicator"><Check size={14} /></div>}
                        </div>
                      );
                    })}
                  </div>
                  {filteredUsers.length > 6 && (
                    <div className="toggle-users-container">
                      <button className="toggle-users-btn-compact" onClick={() => setShowAllUsers(!showAllUsers)}>
                      {showAllUsers ? (
                        <><XBtn onClick={() => setShowAllUsers(false)} label={t("feed.showLess")} variant="ghost" size="sm" /></>
                      ) : (
                        <><span>{t("feed.showAllUsers", { count: filteredUsers.length })}</span><Users size={14} /></>
                      )}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="share-section-compact">
            <div className="section-header-compact">
              <img src="/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png" alt="ShareFlow" style={{ width: '18px', height: '18px', transform: 'rotate(30deg)' }} />
              <h4>{t("feed.shareOnSocialMedia")}</h4>
            </div>
            <div className="social-share-grid-compact">
              <div className="social-share-row">
                <button className="social-share-btn-compact whatsapp clay-press" onClick={() => handleShareToSocial('whatsapp')}><MessageCircle size={22} /><span>WhatsApp</span></button>
                <button className="social-share-btn-compact facebook clay-press" onClick={() => handleShareToSocial('facebook')}><Facebook size={22} /><span>Facebook</span></button>
                <button className="social-share-btn-compact linkedin clay-press" onClick={() => handleShareToSocial('linkedin')}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  <span>LinkedIn</span>
                </button>
              </div>
              <div className="social-share-row">
                <button className="social-share-btn-compact instagram clay-press" onClick={() => handleShareToSocial('instagram')}><Instagram size={22} /><span>Instagram</span></button>
                <button className="social-share-btn-compact telegram clay-press" onClick={() => handleShareToSocial('telegram')}><Send size={22} /><span>Telegram</span></button>
                <button className="social-share-btn-compact copy clay-press" onClick={() => handleShareToSocial('copy')}><Copy size={22} /><span>{t("userPublicProfile.copyLink")}</span></button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer-compact" style={{ borderTop: '1px solid #e0ede8', padding: '12px 20px' }}>
          <button className="cancel-btn-compact" onClick={onClose}>{t("postModal.cancel")}</button>
          <button className="send-btn-compact" onClick={handleConfirm} disabled={!selectedUser || isSending}>
            {isSending ? <div className="mini-spinner" style={{ margin: '0 auto' }}></div> : <><Send size={16} />{t("messageModal.sendMessage")}</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== FOLLOWERS/FOLLOWING MODAL ====================

const FollowersFollowingModal = ({ isOpen, onClose, userId, type, getImageUrl, navigate }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(type || 'followers');

  useEffect(() => { if (isOpen && userId) loadUsers(); }, [isOpen, userId, activeTab]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const endpoint = activeTab === 'followers' ? `/users/${userId}/followers` : `/users/${userId}/following`;
      const data = await apiRequest(endpoint);
      let usersList = [];
      if (Array.isArray(data)) usersList = data;
      else if (data.followers && Array.isArray(data.followers)) usersList = data.followers;
      else if (data.following && Array.isArray(data.following)) usersList = data.following;
      else if (data.users && Array.isArray(data.users)) usersList = data.users;
      setUsers(usersList.filter(u => u && (u._id || u.id)));
    } catch (error) { setUsers([]); }
    finally { setLoading(false); }
  };

  if (!isOpen) return null;
  return (
    <div className="modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="modal-content followers-modal clay-modal-enter" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-tabs">
            <button className={`modal-tab ${activeTab === 'followers' ? 'active' : ''}`} onClick={() => setActiveTab('followers')}><Users size={18} />{t("userPublicProfile.followersLabel")}</button>
            <button className={`modal-tab ${activeTab === 'following' ? 'active' : ''}`} onClick={() => setActiveTab('following')}><UserCheck size={18} />{t("userPublicProfile.followingLabel")}</button>
          </div>
          <XBtn onClick={onClose} label={t("feed.close")} variant="default" size="md" />
        </div>
        <div className="modal-body followers-list">
          {loading ? (
            <div className="loading-state"><div className="spinner-small"></div><span>{t("feed.loading")}</span></div>
          ) : users.length === 0 ? (
            <div className="no-users"><Users size={48} style={{ color: '#ccc', marginBottom: '16px' }} /><p>{activeTab === 'followers' ? t("feed.noFollowersYet") : t("feed.noFollowingYet")}</p></div>
          ) : (
            users.map((user, index) => {
              const userData = user.user || user.follower || user.following || user;
              const uid = userData._id || userData.id || `user-${index}`;
              const userName = userData.organizationName || userData.name || userData.username || t("feed.unknown");
              return (
                <div key={`${activeTab}-${uid}`} className="follower-item" onClick={() => { navigate(`/profile/${uid}`); onClose(); }}>
                  <img src={getImageUrl(userData.profileImage)} alt={userName} className="follower-avatar" onError={handleImageError} />
                  <div className="follower-info">
                    <div className="follower-name">{userName}</div>
                    <div className="follower-type">{userData.userType === 'organizer' ? t("userPublicProfile.organization") : t("userPublicProfile.communityMember")}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

// ==================== EDIT POST MODAL ====================

const EditPostModal = ({ isOpen, post, onClose, onSave, getImageUrl, isSaving, newImages = [], onRemoveNewImage, onRequestCrop, onClearNewImages }) => {
  const { t } = useTranslation();
  const [editContent, setEditContent] = useState('');
  const [keepExistingImages, setKeepExistingImages] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (post && isOpen) { setEditContent(post.content || ''); setKeepExistingImages(true); }
  }, [post, isOpen]);

  useEffect(() => {
    if (!isOpen || !post) return;
    if (newImages.length === 0 && post.images?.length) setKeepExistingImages(true);
  }, [newImages.length, isOpen, post]);

  const previewUrls = useMemo(() => newImages.map(f => URL.createObjectURL(f)), [newImages]);
  useEffect(() => () => { previewUrls.forEach(url => URL.revokeObjectURL(url)); }, [previewUrls]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = '';
    setKeepExistingImages(false);
    if (onRequestCrop) onRequestCrop(files);
  };

  const handleRemoveNewImage = (index) => {
    if (onRemoveNewImage) onRemoveNewImage(index);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!editContent.trim() && newImages.length === 0 && (!post.images || post.images.length === 0)) return;
    onSave({ content: editContent, keepExistingImages });
  };

  if (!isOpen || !post) return null;
  return (
    <div className="modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="modal-content edit-post-modal clay-modal-enter" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px', width: '95%' }}>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Edit2 size={20} color="var(--green-700)" /><h3 style={{ margin: 0 }}>Edit Post</h3></div>
          <XBtn onClick={onClose} label="Close" variant="default" size="md" disabled={isSaving} />
        </div>
        <form className="modal-body" onSubmit={handleSubmit} style={{ padding: '20px' }}>
          <textarea className="edit-post-textarea" value={editContent} onChange={(e) => setEditContent(e.target.value)} placeholder={t("feed.whatsOnMind")} rows={4} style={{ width: '100%', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '15px', resize: 'vertical', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }} />
          {post.images && post.images.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                <p style={{ margin: 0, fontSize: '13px', color: 'var(--ink-400)', fontWeight: '500' }}>Current images ({post.images.length})</p>
                {keepExistingImages && newImages.length === 0 && <span style={{ fontSize: '12px', color: 'var(--green-700)' }}>✓ Will be kept</span>}
                {!keepExistingImages && newImages.length > 0 && <span style={{ fontSize: '12px', color: '#f59e0b' }}>Will be replaced</span>}
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {post.images.map((img, idx) => (
                  <img key={idx} src={getImageUrl(img)} alt={`Current ${idx + 1}`} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '2px solid', borderColor: keepExistingImages && newImages.length === 0 ? 'var(--green-700)' : '#e0e0e0', opacity: !keepExistingImages && newImages.length > 0 ? 0.4 : 1 }} onError={handleImageError} />
                ))}
              </div>
              {!keepExistingImages && newImages.length > 0 && (
                <button type="button" onClick={() => { if (onClearNewImages) onClearNewImages(); setKeepExistingImages(true); if (fileInputRef.current) fileInputRef.current.value = ''; }} style={{ marginTop: '8px', background: 'none', border: '1px solid var(--green-700)', color: 'var(--green-700)', borderRadius: '6px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' }}>↩ Keep original images</button>
              )}
            </div>
          )}
          {previewUrls.length > 0 && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--ink-400)', fontWeight: '500' }}>New images (will replace current)</p>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {previewUrls.map((url, idx) => (
                  <div key={idx} style={{ position: 'relative' }}>
                    <img src={url} alt={`New ${idx + 1}`} style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px', border: '2px solid var(--green-700)' }} />
                    <div style={{ position: 'absolute', top: -8, right: -8 }}>
                      <XBtn onClick={() => handleRemoveNewImage(idx)} label="Remove" variant="danger" size="sm" style={{ borderRadius: '50%', width: 22, height: 22, minWidth: 22, padding: 0 }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div style={{ marginTop: '16px' }}>
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleFileChange} style={{ display: 'none' }} id="edit-post-image-upload" />
            <label htmlFor="edit-post-image-upload" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', border: '1px dashed var(--green-700)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--green-700)' }}>
              <Camera size={16} />{post.images && post.images.length > 0 ? t('feed.replaceImages') : t('feed.addImages')}
            </label>
          </div>
        </form>
        <div className="modal-footer" style={{ padding: '16px 20px', borderTop: '1px solid #f0f0f0' }}>
          <button className="cancel-btn" onClick={onClose} disabled={isSaving} style={{ marginRight: '8px' }}>Cancel</button>
          <button className="save-btn clay-press clay-focus-glow" onClick={handleSubmit} disabled={isSaving || (!editContent.trim() && newImages.length === 0 && (!post.images || post.images.length === 0))} style={{ background: 'var(--green-700)', color: 'white', border: 'none', borderRadius: '8px', padding: '8px 20px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px', opacity: isSaving ? 0.7 : 1 }}>
            {isSaving ? <><div className="mini-spinner"></div>Saving...</> : <><Check size={16} />Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
};

// ==================== IMAGE CROP MODAL (fixed drag & added X button) ====================

const ImageCropModal = ({ isOpen, imageFile, imageUrl, onConfirm, onCancel }) => {
  const imgNatural = useRef({ w: 1, h: 1 });
  const FRAME = 480;
  const frameRef = useRef(null);

  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(1);
  const [draggingUI, setDraggingUI] = useState(false);

  const dragging = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!isOpen || !imageUrl) return;
    const img = new Image();
    img.onload = () => {
      imgNatural.current = { w: img.naturalWidth, h: img.naturalHeight };
      const s = Math.max(FRAME / img.naturalWidth, FRAME / img.naturalHeight);
      setMinScale(s);
      setScale(s);
      setOffset({ x: (FRAME - img.naturalWidth * s) / 2, y: (FRAME - img.naturalHeight * s) / 2 });
    };
    img.src = imageUrl;
  }, [isOpen, imageUrl]);

  const clamp = (ox, oy, s) => {
    const iw = imgNatural.current.w * s;
    const ih = imgNatural.current.h * s;
    return {
      x: Math.min(0, Math.max(ox, FRAME - iw)),
      y: Math.min(0, Math.max(oy, FRAME - ih)),
    };
  };

  const getClient = (e) => {
    if (e.touches && e.touches.length > 0) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    dragging.current = true;
    setDraggingUI(true);
    lastPos.current = getClient(e);
    const el = frameRef.current;
    if (el?.setPointerCapture && e.pointerId != null) {
      try { el.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    window.addEventListener('mousemove', onPointerMove);
    window.addEventListener('mouseup', onPointerUp);
    window.addEventListener('touchmove', onPointerMove, { passive: false });
    window.addEventListener('touchend', onPointerUp);
  };

  const onPointerMove = (e) => {
    if (!dragging.current) return;
    e.preventDefault();
    const cur = getClient(e);
    setOffset(prev => clamp(prev.x + cur.x - lastPos.current.x, prev.y + cur.y - lastPos.current.y, scale));
    lastPos.current = cur;
  };

  const onPointerUp = (e) => {
    if (!dragging.current) return;
    dragging.current = false;
    setDraggingUI(false);
    const el = frameRef.current;
    if (el?.releasePointerCapture && e?.pointerId != null) {
      try { el.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    window.removeEventListener('mousemove', onPointerMove);
    window.removeEventListener('mouseup', onPointerUp);
    window.removeEventListener('touchmove', onPointerMove);
    window.removeEventListener('touchend', onPointerUp);
  };

  const onWheel = (e) => {
    e.preventDefault();
    const next = Math.max(minScale, Math.min(scale + (e.deltaY < 0 ? 0.12 : -0.12), minScale * 4));
    setScale(next);
    setOffset(o => clamp(o.x, o.y, next));
  };

  const doZoom = (dir) => {
    const next = Math.max(minScale, Math.min(scale + dir * 0.18, minScale * 4));
    setScale(next);
    setOffset(o => clamp(o.x, o.y, next));
  };

  const handleApply = () => {
    const canvas = document.createElement('canvas');
    canvas.width = FRAME;
    canvas.height = FRAME;
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const nw = img.naturalWidth;
      const nh = img.naturalHeight;
      const s = scale;
      const ox = offset.x;
      const oy = offset.y;
      const srcLeft = -ox / s;
      const srcTop = -oy / s;
      const srcW = FRAME / s;
      const srcH = FRAME / s;
      const sx = Math.max(0, Math.min(nw, srcLeft));
      const sy = Math.max(0, Math.min(nh, srcTop));
      const ex = Math.max(0, Math.min(nw, srcLeft + srcW));
      const ey = Math.max(0, Math.min(nh, srcTop + srcH));
      let sw = ex - sx;
      let sh = ey - sy;
      if (sw <= 0 || sh <= 0) {
        ctx.drawImage(img, 0, 0, nw, nh, 0, 0, FRAME, FRAME);
      } else {
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, FRAME, FRAME);
      }
      canvas.toBlob((blob) => {
        if (!blob) { onConfirm(imageFile); return; }
        onConfirm(new File([blob], imageFile?.name || 'photo.jpg', { type: 'image/jpeg', lastModified: Date.now() }));
      }, 'image/jpeg', 0.92);
    };
    img.onerror = () => { onConfirm(imageFile); };
    img.src = imageUrl;
  };

  if (!isOpen || !imageUrl) return null;

  const pct = Math.round(scale / minScale * 100);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onCancel}>
      <div style={{ background: '#1a1a1a', borderRadius: '18px', overflow: 'hidden', width: FRAME + 40, maxWidth: 'calc(100vw - 32px)', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 18px', background: '#111', borderBottom: '1px solid #2a2a2a' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Camera size={16} color="var(--green-700)" />
            <span style={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Edit Photo</span>
          </div>
          <span style={{ fontSize: '11px', color: '#555' }}>Drag to move · scroll or +/− to zoom</span>
          <XBtn onClick={onCancel} label="Close" variant="modal" size="md" style={{ borderRadius: '7px' }} />
        </div>

        <div style={{ padding: '18px 20px 10px', background: '#111', display: 'flex', justifyContent: 'center' }}>
          <div
            ref={frameRef}
            style={{ width: FRAME, height: FRAME, overflow: 'hidden', position: 'relative', borderRadius: '10px', cursor: draggingUI ? 'grabbing' : 'grab', border: '2px solid var(--green-700)', boxShadow: '0 0 0 4px rgba(50,97,77,0.18)', touchAction: 'none', userSelect: 'none' }}
            onPointerDown={onPointerDown}
            onWheel={onWheel}
          >
            <img src={imageUrl} draggable={false} alt="edit"
              style={{ position: 'absolute', left: offset.x, top: offset.y, width: imgNatural.current.w * scale, height: imgNatural.current.h * scale, pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
              {[1,2].map(i => (
                <React.Fragment key={i}>
                  <div style={{ position:'absolute', left:`${i*33.33}%`, top:0, bottom:0, width:'1px', background:'rgba(255,255,255,0.15)' }} />
                  <div style={{ position:'absolute', top:`${i*33.33}%`, left:0, right:0, height:'1px', background:'rgba(255,255,255,0.15)' }} />
                </React.Fragment>
              ))}
              {[{top:0,left:0,bt:'3px solid var(--green-700)',bb:'none',bl:'3px solid var(--green-700)',br:'none'},
                {top:0,right:0,bt:'3px solid var(--green-700)',bb:'none',bl:'none',br:'3px solid var(--green-700)'},
                {bottom:0,left:0,bt:'none',bb:'3px solid var(--green-700)',bl:'3px solid var(--green-700)',br:'none'},
                {bottom:0,right:0,bt:'none',bb:'3px solid var(--green-700)',bl:'none',br:'3px solid var(--green-700)'},
              ].map((s,i) => (
                <div key={i} style={{ position:'absolute', width:22, height:22, ...s, borderTop:s.bt, borderBottom:s.bb, borderLeft:s.bl, borderRight:s.br }} />
              ))}
            </div>
          </div>
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'8px 20px', background:'#111' }}>
          <button onClick={() => doZoom(-1)} style={{ background:'#2a2a2a', border:'1px solid #3a3a3a', color:'#fff', borderRadius:'7px', width:'32px', height:'32px', fontSize:'18px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
          <input type="range" min={Math.round(minScale*100)} max={Math.round(minScale*400)} value={Math.round(scale*100)}
            onChange={e => { const s=Number(e.target.value)/100; setScale(s); setOffset(o=>clamp(o.x,o.y,s)); }}
            style={{ flex:1, accentColor:'var(--green-700)' }} />
          <button onClick={() => doZoom(1)} style={{ background:'#2a2a2a', border:'1px solid #3a3a3a', color:'#fff', borderRadius:'7px', width:'32px', height:'32px', fontSize:'18px', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
          <span style={{ fontSize:'12px', color:'#555', minWidth:'38px', textAlign:'right' }}>{pct}%</span>
        </div>

        <div style={{ display:'flex', justifyContent:'flex-end', gap:'8px', padding:'12px 20px', background:'#111', borderTop:'1px solid #2a2a2a' }}>
          <button onClick={() => { const s=minScale; setScale(s); setOffset({ x:(FRAME-imgNatural.current.w*s)/2, y:(FRAME-imgNatural.current.h*s)/2 }); }}
            style={{ background:'none', border:'1px solid #3a3a3a', color:'#888', borderRadius:'8px', padding:'8px 14px', cursor:'pointer', fontSize:'13px' }}>Reset</button>
          <button onClick={onCancel} style={{ background:'none', border:'1px solid #3a3a3a', color:'#888', borderRadius:'8px', padding:'8px 14px', cursor:'pointer', fontSize:'13px' }}>Cancel</button>
          <button onClick={handleApply} style={{ background:'var(--green-700)', color:'#fff', border:'none', borderRadius:'8px', padding:'8px 22px', cursor:'pointer', fontSize:'13px', fontWeight:'700' }}>Use Photo</button>
        </div>
      </div>
    </div>
  );
};

const ModerationWarningModal = ({ isOpen, onClose, reason }) => {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay clay-modal-backdrop" onClick={onClose}>
      <div className="modal-content moderation-warning clay-modal-enter" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px', textAlign: 'center', padding: '30px' }}>
        <div style={{ marginBottom: '20px' }}><ShieldAlert size={64} color="#ef4444" style={{ margin: '0 auto' }} /></div>
        <h3 style={{ color: '#ef4444', marginBottom: '15px', fontSize: '20px' }}>Content Blocked</h3>
        <p style={{ color: '#666', marginBottom: '20px', lineHeight: '1.5' }}>{reason || 'This image has been blocked because it may contain 18+ or violent content.'}</p>
        <p style={{ color: '#888', fontSize: '14px', marginBottom: '25px' }}>Please only upload appropriate images.</p>
        <button onClick={onClose} style={{ background: 'var(--green-700)', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 30px', fontSize: '16px', cursor: 'pointer', fontWeight: '500' }}>Got it</button>
      </div>
    </div>
  );
};

// ==================== MOBILE RECOMMENDED STRIP ====================

const MobileRecommendedStrip = ({ suggestedUsers, following, isLoadingFollow, getImageUrl, navigate, sendFollowRequest, setSuggestedUsers }) => {
  const { t } = useTranslation();
  if (suggestedUsers.length === 0) return null;

  return (
    <div style={{
      background: 'var(--fg-glass-fill, linear-gradient(155deg, rgba(255,255,255,0.14), rgba(255,255,255,0.05)))',
      borderBottom: '1px solid rgba(255,255,255,0.12)',
      padding: '12px 14px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
        <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--fg-text-hi, #fdfdf9)' }}>{t("feed.recommendedForYou")}</span>
        <button
          onClick={() => navigate('/search')}
          style={{ fontSize: '12px', color: 'var(--fg-gold-bright, #ffd873)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
        >
          {t("feed.seeAll")}
        </button>
      </div>
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
        overflowX: 'auto',
        overflowY: 'hidden',
        paddingBottom: '4px',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}>
        {suggestedUsers.slice(0, 10).map((suggestedUser, index) => {
          const userData = suggestedUser.user || suggestedUser;
          const uid = getSafeUserId(userData);
          const userName = getSafeUserName(userData);
          const isFollowingUser = following.some(f => getSafeUserId(f.user || f) === uid);

          return (
            <div
              key={`mob-rec-${uid || index}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 0,
                width: '72px',
                background: 'rgba(255,255,255,0.06)',
                borderRadius: '12px',
                padding: '10px 6px 8px',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <div
                style={{ position: 'relative', cursor: 'pointer' }}
                onClick={() => uid && navigate(`/profile/${uid}`)}
              >
                <img
                  src={getImageUrl(userData.profileImage)}
                  alt={userName}
                  onError={handleImageError}
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid rgba(255,255,255,0.18)',
                  }}
                />
                {userData.userType === 'organizer' && userData.isVerified && (
                  <div style={{
                    position: 'absolute', bottom: 0, right: 0,
                    background: 'var(--green-700)', color: '#fff',
                    borderRadius: '50%', width: '14px', height: '14px',
                    fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>✓</div>
                )}
              </div>
              <div style={{
                fontSize: '11px', fontWeight: 600, color: 'var(--fg-text-hi, #fdfdf9)',
                textAlign: 'center', width: '100%',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {userName.length > 9 ? userName.substring(0, 9) + '…' : userName}
              </div>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!isFollowingUser && uid) {
                    await sendFollowRequest(uid);
                    setSuggestedUsers(prev => prev.filter(u => getSafeUserId(u.user || u) !== uid));
                  }
                }}
                disabled={isFollowingUser || isLoadingFollow[uid] || !uid}
                style={{
                  width: '100%',
                  padding: '4px 0',
                  fontSize: '11px',
                  borderRadius: '20px',
                  border: isFollowingUser ? '1px solid transparent' : '1px solid rgba(255,203,71,0.4)',
                  background: isFollowingUser ? 'var(--fg-gold-gradient, linear-gradient(150deg, #ffd873, #d69a17 65%, #a6740f))' : 'rgba(255,255,255,0.06)',
                  color: isFollowingUser ? '#3a2a05' : 'var(--fg-gold-bright, #ffd873)',
                  cursor: isFollowingUser ? 'default' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  fontFamily: 'inherit',
                }}
              >
                {isLoadingFollow[uid]
                  ? <div className="mini-spinner" style={{ width: '10px', height: '10px' }}></div>
                  : isFollowingUser
                    ? <UserCheck size={12} />
                    : <UserPlus size={12} />
                }
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== MAIN FEED COMPONENT ====================

function Feed() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useIsMobile();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [replyInputs, setReplyInputs] = useState({});
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostImages, setNewPostImages] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [isUpdatingPost, setIsUpdatingPost] = useState(false);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [recommendedPrograms, setRecommendedPrograms] = useState([]);
  const [recommendedProgramsPersonalized, setRecommendedProgramsPersonalized] = useState(false);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [followRequests, setFollowRequests] = useState([]);
  const [userPostsCount, setUserPostsCount] = useState(0);
  const [isLoadingFollow, setIsLoadingFollow] = useState({});
  const [isLiking, setIsLiking] = useState({});
  const [isSaving, setIsSaving] = useState({});
  const [isCommenting, setIsCommenting] = useState({});
  const [showMultiImageLightbox, setShowMultiImageLightbox] = useState(false);
  const [currentImages, setCurrentImages] = useState([]);
  const [currentLightboxIndex, setCurrentLightboxIndex] = useState(0);
  const [showComments, setShowComments] = useState({});
  const [showUserSelector, setShowUserSelector] = useState(false);
  const [currentPostForSharing, setCurrentPostForSharing] = useState(null);
  const [isSendingPost, setIsSendingPost] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [postObjToDelete, setPostObjToDelete] = useState(null);
  const [isDeletingPost, setIsDeletingPost] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState({ type: '', id: '' });
  const [lastCommentAction, setLastCommentAction] = useState({});
  const [showFollowersModal, setShowFollowersModal] = useState(false);
  const [followersModalUserId, setFollowersModalUserId] = useState(null);
  const [followersModalType, setFollowersModalType] = useState('followers');
  const [ignoredSocketUpdates, setIgnoredSocketUpdates] = useState({});
  const [coverImageLoaded, setCoverImageLoaded] = useState(true);
  const [activeCommentPostId, setActiveCommentPostId] = useState(null);
  const [linkCopiedNotification, setLinkCopiedNotification] = useState(false);
  const [activeEmojiComment, setActiveEmojiComment] = useState(null);
  const [showDeleteCommentModal, setShowDeleteCommentModal] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [postIdForCommentDelete, setPostIdForCommentDelete] = useState(null);
  const [showModerationWarning, setShowModerationWarning] = useState(false);
  const [moderationReason, setModerationReason] = useState('');
  const [moderatingImages, setModeratingImages] = useState(false);
  const [blockedImages, setBlockedImages] = useState([]);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropPendingFiles, setCropPendingFiles] = useState([]);
  const [cropCurrentIndex, setCropCurrentIndex] = useState(0);
  const [cropCurrentUrl, setCropCurrentUrl] = useState(null);
  const [editPostNewImages, setEditPostNewImages] = useState([]);
  const cropIntentRef = useRef('newPost');

  const socketRef = useRef(null);
  const postsContainerRef = useRef(null);
  const userId = user?._id || null;

  // ==================== GET IMAGE URL ====================

  const getImageUrl = useCallback((imagePath) => {
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined') return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.includes('uploads') || imagePath.includes('nkarner')) {
      const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
      return `${GREENWICH_BACKEND_URL}${cleanPath}`;
    }
    return '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png';
  }, []);

  const getCoverImageUrl = useCallback((imagePath, forceReload = false) => {
    if (!imagePath || imagePath === 'null' || imagePath === 'undefined') return null;
    if (imagePath.startsWith('http')) return imagePath;
    if (imagePath.startsWith('/uploads/') || imagePath.startsWith('/nkarner/')) {
      const fullUrl = `${GREENWICH_BACKEND_URL}${imagePath}`;
      return forceReload ? `${fullUrl}?refresh=1` : fullUrl;
    }
    if (imagePath.includes('uploads') || imagePath.includes('nkarner')) {
      const fullUrl = `${GREENWICH_BACKEND_URL}/${imagePath}`;
      return forceReload ? `${fullUrl}?refresh=1` : fullUrl;
    }
    return null;
  }, []);

  // ==================== LOAD SAVED INTERACTIONS ====================

  useEffect(() => {
    if (userId) {
      const savedInteractions = loadUserInteractions(userId);
      setPosts(prevPosts => prevPosts.map(post => {
        const updatedPost = { ...post };
        if (savedInteractions.likes[post._id] && !updatedPost.likes?.includes(userId)) updatedPost.likes = [...(updatedPost.likes || []), userId];
        if (savedInteractions.saves[post._id] && !updatedPost.saves?.includes(userId)) updatedPost.saves = [...(updatedPost.saves || []), userId];
        return updatedPost;
      }));
    }
  }, [userId]);

  const toggleComments = (postId) => {
    setShowComments(prev => {
      const newState = {};
      Object.keys(prev).forEach(id => { newState[id] = false; });
      newState[postId] = !prev[postId];
      setActiveCommentPostId(newState[postId] ? postId : null);
      return newState;
    });
  };

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return '';
      const now = new Date();
      const diff = now - d;
      const minutes = Math.floor(diff / 60000), hours = Math.floor(diff / 3600000), days = Math.floor(diff / 86400000);
      if (minutes < 1) return t('pages.feed.justNow');
      if (minutes < 60) return t('pages.feed.minAgo', { n: minutes });
      if (hours < 24) return t('pages.feed.hourAgo', { n: hours });
      if (days < 7) return t('pages.feed.dayAgo', { n: days });
      return d.toLocaleDateString(i18n.language === 'hy' ? 'hy-AM' : 'en-US', { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
    } catch (error) { return ''; }
  };

  // ==================== LOAD FEED ====================

  const loadFeed = useCallback(async () => {
    try {
      setLoading(true); setError(null);
      const data = await apiRequest('/posts');
      let rawPosts = [];
      if (Array.isArray(data)) rawPosts = data;
      else if (data.posts && Array.isArray(data.posts)) rawPosts = data.posts;
      else if (data.data && Array.isArray(data.data)) rawPosts = data.data;
      else if (typeof data === 'object') { for (const key in data) { if (Array.isArray(data[key])) { rawPosts = data[key]; break; } } }
      const validPosts = rawPosts.map(post => normalizePost(post)).filter(post => post && post._id && post.author && post.author._id);
      setPosts(validPosts);
      if (validPosts.length === 0) setError(t('pages.feed.noPostsYet'));
    } catch (error) {
      console.error('Error loading feed:', error);
      setError(error.message || t('pages.feed.loadFailed'));
    } finally { setLoading(false); }
  }, [t]);

  // ==================== LOAD SUGGESTED USERS ====================

  const loadSuggestedUsers = useCallback(async () => {
    try {
      let suggestedUsersList = [];
      try {
        const data = await apiRequest('/users/suggested');
        if (data.suggested && Array.isArray(data.suggested)) suggestedUsersList = data.suggested;
        else if (data.users && Array.isArray(data.users)) suggestedUsersList = data.users;
        else if (Array.isArray(data)) suggestedUsersList = data;
      } catch {
        try {
          const allUsersData = await apiRequest('/users');
          if (Array.isArray(allUsersData)) suggestedUsersList = allUsersData;
          else if (allUsersData.users && Array.isArray(allUsersData.users)) suggestedUsersList = allUsersData.users;
        } catch (innerError) { console.error('Error loading all users:', innerError); }
      }
      const currentUserId = user?._id || user?.id;
      const filteredSuggestedUsers = suggestedUsersList.filter(u => getSafeUserId(u) !== currentUserId).map(u => ({ ...u, userType: u.userType || 'player', isVerified: u.isVerified || false, organizationName: u.organizationName || u.name || u.username, name: u.name || u.username, username: u.username, profileImage: u.profileImage || '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png', isOnline: u.isOnline || false }));
      setSuggestedUsers(filteredSuggestedUsers);
    } catch (error) { setSuggestedUsers([]); }
  }, [user]);

  // Reuses the existing tag-based program recommender (same one behind the
  // "Recommended for you" button on the Programs page) to surface a couple of
  // matching programs right in the Feed sidebar, so users see them without an
  // extra click. Falls back silently (empty widget) if it fails — this is a
  // secondary panel, not core feed functionality.
  const loadRecommendedPrograms = useCallback(async () => {
    try {
      const data = await apiRequest('/programs/recommended?limit=3');
      const list = Array.isArray(data?.programs) ? data.programs : [];
      setRecommendedPrograms(list);
      setRecommendedProgramsPersonalized(!!data?.personalized);
    } catch (error) { setRecommendedPrograms([]); }
  }, []);

  const loadFollowing = async () => {
    try {
      const data = await apiRequest('/users/following');
      let followingList = [];
      if (Array.isArray(data)) followingList = data;
      else if (data.following && Array.isArray(data.following)) followingList = data.following;
      else if (data.data && Array.isArray(data.data)) followingList = data.data;
      else if (data.users && Array.isArray(data.users)) followingList = data.users;
      followingList = followingList.filter(item => item && (item._id || item.id));
      setFollowing(followingList.map((u, index) => { const userData = u.user || u.following || u; return { ...u, tempId: u._id || u.id || `temp-${index}`, user: { ...userData, isOnline: userData.isOnline || false } }; }));
    } catch (error) { setFollowing([]); }
  };

  const loadFollowers = async () => {
    try {
      const data = await apiRequest('/users/followers');
      let followersList = [];
      if (Array.isArray(data)) followersList = data;
      else if (data.followers && Array.isArray(data.followers)) followersList = data.followers;
      else if (data.data && Array.isArray(data.data)) followersList = data.data;
      else if (data.users && Array.isArray(data.users)) followersList = data.users;
      setFollowers(followersList.filter(item => item && (item._id || item.id)));
    } catch (error) { setFollowers([]); }
  };

  const loadFollowRequests = async () => {
    try {
      const data = await apiRequest('/users/follow-requests');
      if (data.requests && Array.isArray(data.requests)) setFollowRequests(data.requests);
      else if (data.followRequests && Array.isArray(data.followRequests)) setFollowRequests(data.followRequests);
      else if (Array.isArray(data)) setFollowRequests(data);
      else setFollowRequests([]);
    } catch (error) { setFollowRequests([]); }
  };

  const sendFollowRequest = async (targetUserId) => {
    try {
      setIsLoadingFollow(prev => ({ ...prev, [targetUserId]: true }));
      const data = await apiRequest(`/users/${targetUserId}/follow-request`, { method: 'POST' });
      setSuccessMessage(data.message || t('pages.feed.followRequestSent'));
      setSuggestedUsers(prev => {
        const newList = prev.filter(u => getSafeUserId(u.user || u) !== targetUserId);
        const ignoredRecommendations = JSON.parse(localStorage.getItem('ignoredRecommendations') || '[]');
        if (!ignoredRecommendations.includes(targetUserId)) localStorage.setItem('ignoredRecommendations', JSON.stringify([...ignoredRecommendations, targetUserId]));
        return newList;
      });
      setTimeout(() => { loadSuggestedUsers(); }, 1000);
      loadFollowing();
    } catch (error) { setError(error.message || t('pages.feed.failedFollowReq')); }
    finally { setIsLoadingFollow(prev => ({ ...prev, [targetUserId]: false })); }
  };

  const isLiked = (post) => {
    if (!user || !post || !post.likes || !userId) return false;
    return post.likes.includes(userId) || post.likes.some(like => { if (typeof like === 'string') return like === userId; if (typeof like === 'object') return like._id === userId; return false; });
  };

  const isPostSaved = (post) => {
    if (!user || !post || !post.saves || !userId) return false;
    return post.saves.includes(userId) || post.saves.some(save => { if (typeof save === 'string') return save === userId; if (typeof save === 'object') return save._id === userId; return false; });
  };

  const handleLike = async (postId) => {
    if (!postId || !userId) { setError(t('pages.feed.loginToLike')); return; }
    try {
      setIsLiking(prev => ({ ...prev, [postId]: true }));
      const isCurrentlyLiked = isLiked(posts.find(p => p._id === postId));
      setPosts(prevPosts => prevPosts.map(post => {
        if (post && post._id === postId) {
          const currentLikes = Array.isArray(post.likes) ? post.likes : [];
          const updatedPost = { ...post, likes: isCurrentlyLiked ? currentLikes.filter(like => { if (typeof like === 'string') return like !== userId; if (typeof like === 'object') return like._id !== userId; return true; }) : [...currentLikes, userId] };
          if (userId) saveUserInteractions(userId, 'likes', { [postId]: !isCurrentlyLiked });
          return updatedPost;
        }
        return post;
      }));
      await apiRequest(`/posts/${postId}/like`, { method: 'POST' });
    } catch (error) { console.error('Error liking post:', error); setError(error.message || t('pages.feed.failedLike')); loadFeed(); }
    finally { setIsLiking(prev => ({ ...prev, [postId]: false })); }
  };

  const handleSavePost = async (postId) => {
    if (!postId || !userId) return;
    try {
      setIsSaving(prev => ({ ...prev, [postId]: true }));
      const post = posts.find(p => p._id === postId);
      if (!post) return;
      const isCurrentlySaved = isPostSaved(post);
      setPosts(prevPosts => prevPosts.map(p => {
        if (p && p._id === postId) {
          const currentSaves = Array.isArray(p.saves) ? p.saves : [];
          const updatedPost = { ...p, saves: isCurrentlySaved ? currentSaves.filter(save => { if (typeof save === 'string') return save !== userId; if (typeof save === 'object') return save._id !== userId; return true; }) : [...currentSaves, userId] };
          if (userId) saveUserInteractions(userId, 'saves', { [postId]: !isCurrentlySaved });
          return updatedPost;
        }
        return p;
      }));
      try { await apiRequest(`/posts/${postId}/save`, { method: 'POST', body: JSON.stringify({ action: isCurrentlySaved ? 'unsave' : 'save' }) }); }
      catch (error) { try { await apiRequest(`/posts/${postId}/toggle-save`, { method: 'POST' }); } catch { console.log('All save endpoints failed, using localStorage only'); } }
    } catch (error) { console.error('Error saving post:', error); setError(t('pages.feed.failedSave')); loadFeed(); }
    finally { setIsSaving(prev => ({ ...prev, [postId]: false })); }
  };

  const handleComment = async (postId, parentCommentId = null, content = null) => {
    if (!postId || !userId) return;
    const commentKey = parentCommentId ? `${postId}_${parentCommentId}` : postId;
    const comment = content || commentInputs[postId]?.trim();
    if (!comment) return;
    const actionKey = `${commentKey}_${Date.now()}`;
    if (lastCommentAction[commentKey] === actionKey) return;
    setLastCommentAction(prev => ({ ...prev, [commentKey]: actionKey }));
    try {
      setIsCommenting(prev => ({ ...prev, [commentKey]: true }));
      const updateKey = `comment_${postId}`;
      setIgnoredSocketUpdates(prev => ({ ...prev, [updateKey]: Date.now() }));
      setPosts(prevPosts => prevPosts.map(post => {
        if (post && post._id === postId) {
          const currentComments = Array.isArray(post.comments) ? post.comments : [];
          const newComment = { _id: `temp_${Date.now()}`, content: comment, author: { _id: userId, name: user?.name || user?.username || 'User', profileImage: user?.profileImage, userType: user?.userType }, createdAt: new Date().toISOString(), likes: [], parentCommentId: parentCommentId || null };
          if (parentCommentId) {
            const parentIndex = currentComments.findIndex(c => c._id === parentCommentId);
            if (parentIndex !== -1) { const newComments = [...currentComments]; newComments.splice(parentIndex + 1, 0, newComment); return { ...post, comments: newComments }; }
          }
          return { ...post, comments: [newComment, ...currentComments] };
        }
        return post;
      }));
      if (!content) setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      const response = await apiRequest(`/posts/${postId}/comments`, { method: 'POST', body: JSON.stringify({ content: comment, parentCommentId: parentCommentId || null }) });
      if (response.comment) {
        setPosts(prevPosts => prevPosts.map(post => {
          if (post._id === postId) {
            const updatedComments = post.comments?.map(c => c._id && c._id.startsWith('temp_') ? response.comment : c) || [];
            return { ...post, comments: updatedComments };
          }
          return post;
        }));
      }
      setSuccessMessage(parentCommentId ? t('pages.feed.replyPosted') : t('pages.feed.commentAdded'));
      if (parentCommentId) setReplyInputs(prev => { const newInputs = { ...prev }; delete newInputs[parentCommentId]; return newInputs; });
    } catch (error) { console.error('Error adding comment:', error); setError(t('pages.feed.failedComment')); loadFeed(); }
    finally {
      setIsCommenting(prev => ({ ...prev, [commentKey]: false }));
      setTimeout(() => { setLastCommentAction(prev => { const newState = { ...prev }; delete newState[commentKey]; return newState; }); }, 1000);
    }
  };

  const handleCommentLike = async (commentId) => {
    try {
      if (commentId.startsWith('temp_')) return;
      const post = posts.find(p => p.comments?.some(c => c._id === commentId));
      if (!post) return;
      const comment = post.comments?.find(c => c._id === commentId);
      if (!comment) return;
      const isCurrentlyLiked = comment.likes?.includes(userId) || false;
      setPosts(prevPosts => prevPosts.map(p => {
        if (p._id === post._id && p.comments) {
          return { ...p, comments: p.comments.map(c => { if (c._id === commentId) { const currentLikes = Array.isArray(c.likes) ? c.likes : []; return { ...c, likes: isCurrentlyLiked ? currentLikes.filter(id => id !== userId) : [...currentLikes, userId] }; } return c; }) };
        }
        return p;
      }));
      try { await apiRequest(`/comments/${commentId}/like`, { method: 'POST', body: JSON.stringify({ userId, action: isCurrentlyLiked ? 'unlike' : 'like' }) }); }
      catch (error) {
        setPosts(prevPosts => prevPosts.map(p => {
          if (p._id === post._id && p.comments) return { ...p, comments: p.comments.map(c => c._id === commentId ? { ...c, likes: comment.likes || [] } : c) };
          return p;
        }));
        throw error;
      }
    } catch (error) { setError(t('feed.failedToLikeComment')); }
  };

  const handleCommentReply = async (commentId, postId) => {
    const replyText = replyInputs[commentId]?.trim();
    if (!replyText) return;
    try { await handleComment(postId, commentId, replyText); } catch (error) { console.error('Error in handleCommentReply:', error); }
  };

  const handleDeleteComment = async (commentId, postId) => {
    if (!commentId || !postId) return;
    try {
      setPosts(prevPosts => prevPosts.map(post => { if (post._id === postId) { return { ...post, comments: post.comments?.filter(comment => comment._id !== commentId) || [] }; } return post; }));
      setSuccessMessage(t('feed.commentDeletedToast'));
      try { await apiRequest(`/comments/${commentId}`, { method: 'DELETE' }); } catch (error) { console.log('API delete failed, but UI updated:', error); }
      if (replyInputs[commentId]) setReplyInputs(prev => { const newInputs = { ...prev }; delete newInputs[commentId]; return newInputs; });
    } catch (error) { console.error('Error deleting comment:', error); setError(t('feed.failedToDeleteComment')); loadFeed(); }
  };

  const handleDeleteCommentClick = (commentId, postId) => {
    setCommentToDelete(commentId);
    setPostIdForCommentDelete(postId);
    setShowDeleteCommentModal(true);
  };

  // ==================== HANDLE DELETE POST (ADMIN SUPPORT) ====================

  const handleDeletePost = async (postId, adminReason, isAdminDelete) => {
    try {
      setIsDeletingPost(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/posts/${postId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adminDelete: isAdminDelete || false,
          reason: adminReason || undefined,
        }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Failed to delete post');
      }
      setPosts(prevPosts => prevPosts.filter(post => post._id !== postId));
      setShowDeleteModal(false);
      setPostToDelete(null);
      setPostObjToDelete(null);
      setSuccessMessage(isAdminDelete ? t('feed.postDeletedByAdmin') : t('postModal.postDeleted'));
    } catch (error) {
      console.error('Error deleting post:', error);
      setError(error.message || t('feed.failedToDeletePost'));
    } finally { setIsDeletingPost(false); }
  };

  const handleArchivePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/posts/${postId}/archive`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: true }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to archive post');
      setPosts(prevPosts => prevPosts.filter(post => post._id !== postId));
      setSuccessMessage(t('feed.postArchivedToast'));
    } catch (error) {
      console.error('Error archiving post:', error);
      setError(error.message || t('feed.failedToArchivePost'));
    }
  };

  const handleReport = async (reportData) => {
    try {
      await apiRequest('/reports', { method: 'POST', body: JSON.stringify(reportData) });
      setSuccessMessage(t('feed.reportSubmittedToast'));
      setShowReportModal(false);
    } catch (error) { setError(t('feed.failedToSubmitReport')); }
  };

  const handleStartEditPost = (post) => {
    if (!post) return;
    setEditPostNewImages([]);
    cropIntentRef.current = 'newPost';
    setEditingPost(post);
  };
  const handleCancelEditPost = () => {
    setEditingPost(null);
    setEditPostNewImages([]);
    cropIntentRef.current = 'newPost';
  };

  const beginEditPostImageCrop = (files) => {
    if (!files?.length) return;
    const room = 10 - editPostNewImages.length;
    if (room <= 0) { setError(t('feed.maxImagesError')); return; }
    const slice = files.slice(0, room);
    const valid = [];
    for (const file of slice) {
      if (file.size > 15 * 1024 * 1024) { setError(`Image ${file.name} size should be less than 15MB`); continue; }
      if (!file.type.startsWith('image/')) { setError(`File ${file.name} is not an image`); continue; }
      valid.push(file);
    }
    if (valid.length === 0) return;
    cropIntentRef.current = 'editPost';
    setCropPendingFiles(valid);
    setCropCurrentIndex(0);
    if (cropCurrentUrl) URL.revokeObjectURL(cropCurrentUrl);
    setCropCurrentUrl(URL.createObjectURL(valid[0]));
    setShowCropModal(true);
  };

  const removeEditPostNewImage = (index) => {
    setEditPostNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpdatePost = async ({ content, keepExistingImages }) => {
    if (!editingPost) return;
    const newImages = editPostNewImages;
    const trimmed = content.trim();
    if (!trimmed && newImages.length === 0 && (!editingPost.images || editingPost.images.length === 0)) { setError(t('feed.postContentEmptyError')); return; }
    const token = localStorage.getItem('token');
    if (!token) { setError(t('feed.pleaseLogInToEdit')); return; }
    try {
      setIsUpdatingPost(true);
      const formData = new FormData();
      formData.append('content', trimmed);
      if (newImages.length > 0) { newImages.forEach(file => { if (file instanceof File) formData.append('images', file); }); }
      else if (keepExistingImages && editingPost.images && editingPost.images.length > 0) formData.append('keepExistingImages', 'true');
      const response = await fetch(`${API_BASE_URL}/posts/${editingPost._id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to update post');
      const updatedPost = data.post ? normalizePost(data.post) : { ...editingPost, content: trimmed, images: newImages.length > 0 ? editingPost.images : (keepExistingImages ? editingPost.images : []) };
      setPosts(prev => prev.map(p => p._id === updatedPost._id ? updatedPost : p));
      setSuccessMessage(t('feed.postUpdatedToast'));
      handleCancelEditPost();
    } catch (error) { setError(error.message || t('feed.failedToUpdatePost')); }
    finally { setIsUpdatingPost(false); }
  };

  const sendPostAsMessage = async (targetUser, post) => {
    try {
      if (!post || !targetUser || !targetUser._id) { setError(t('feed.cannotSendPostError')); return false; }
      setIsSendingPost(true); setError(null);
      let conversationId;
      try {
        const createConvData = await apiRequest('/conversations', { method: 'POST', body: JSON.stringify({ participantIds: [targetUser._id], isGroup: false }) });
        conversationId = createConvData.conversation?._id;
      } catch {
        try {
          const existingConvs = await apiRequest('/conversations');
          const existingConv = existingConvs.find(conv => conv.participants?.some(p => p._id === targetUser._id) && conv.participants?.length === 2);
          if (existingConv) conversationId = existingConv._id;
          else throw new Error('No existing conversation found');
        } catch { throw new Error('Failed to create or find conversation'); }
      }
      if (!conversationId) throw new Error('Failed to get conversation ID');
      const postUrl = `${window.location.origin}/post/${post._id}`;
      const postAuthorName = getSafeUserName(post.author);
      await apiRequest(`/conversations/${conversationId}/messages`, { method: 'POST', body: JSON.stringify({ content: `📤 Shared a post from ${postAuthorName}`, metadata: { type: 'shared_post', postId: post._id, postUrl, postAuthorId: post.author?._id, postAuthorName, postAuthorImage: post.author?.profileImage, postContent: post.content, postImage: post.images && post.images.length > 0 ? post.images[0] : null, postImages: post.images || [], sharedBy: { _id: user?._id, name: user?.organizationName || user?.name, profileImage: user?.profileImage } } }) });
      setSuccessMessage(`✅ Post sent to ${targetUser.organizationName || targetUser.name}!`);
      setShowUserSelector(false); setCurrentPostForSharing(null);
      return true;
    } catch (error) { setError(`Failed to send post: ${error.message}`); return false; }
    finally { setIsSendingPost(false); }
  };

  const handleSendPostButtonClick = (postId) => {
    const postToShare = posts.find(p => p._id === postId);
    if (!postToShare) { setError(t('feed.postNotFoundError')); return; }
    setCurrentPostForSharing(postToShare);
    setShowUserSelector(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (files.length + newPostImages.length > 10) { setError(t('feed.maxImagesError')); return; }
    e.target.value = '';

    const validFiles = [];
    for (const file of files) {
      if (file.size > 15 * 1024 * 1024) { setError(`Image ${file.name} size should be less than 15MB`); continue; }
      if (!file.type.startsWith('image/')) { setError(`File ${file.name} is not an image`); continue; }
      validFiles.push(file);
    }
    if (validFiles.length === 0) return;

    cropIntentRef.current = 'newPost';
    setCropPendingFiles(validFiles);
    setCropCurrentIndex(0);
    setCropCurrentUrl(URL.createObjectURL(validFiles[0]));
    setShowCropModal(true);
  };

  const handleCropConfirm = async (croppedFile) => {
    const intent = cropIntentRef.current;
    const advanceCropQueue = () => {
      if (cropCurrentUrl) URL.revokeObjectURL(cropCurrentUrl);
      const nextIdx = cropCurrentIndex + 1;
      if (nextIdx < cropPendingFiles.length) {
        setCropCurrentIndex(nextIdx);
        setCropCurrentUrl(URL.createObjectURL(cropPendingFiles[nextIdx]));
      } else {
        setShowCropModal(false);
        setCropPendingFiles([]); setCropCurrentIndex(0); setCropCurrentUrl(null);
        cropIntentRef.current = 'newPost';
      }
    };

    try {
      const moderationResult = await imageModerator.isImageSafe(croppedFile);
      if (!moderationResult.safe) {
        setShowCropModal(false);
        if (cropCurrentUrl) URL.revokeObjectURL(cropCurrentUrl);
        setModerationReason(moderationResult.reason || 'Image may contain 18+ or violent content');
        setShowModerationWarning(true);
        const nextIdx = cropCurrentIndex + 1;
        if (nextIdx < cropPendingFiles.length) {
          setTimeout(() => {
            setCropCurrentIndex(nextIdx);
            setCropCurrentUrl(URL.createObjectURL(cropPendingFiles[nextIdx]));
            setShowCropModal(true);
          }, 500);
        } else {
          setCropPendingFiles([]); setCropCurrentIndex(0); setCropCurrentUrl(null);
          cropIntentRef.current = 'newPost';
        }
        return;
      }
    } catch { /* allow on moderation error */ }

    let finalFile = croppedFile;
    try { finalFile = await resizeImage(croppedFile, 1200, 1200, 0.85); } catch { finalFile = croppedFile; }

    if (intent === 'editPost') {
      setEditPostNewImages(prev => [...prev, finalFile].slice(0, 10));
    } else {
      setNewPostImages(prev => [...prev, finalFile].slice(0, 10));
    }
    setError(null);

    advanceCropQueue();
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    if (cropCurrentUrl) URL.revokeObjectURL(cropCurrentUrl);
    setCropPendingFiles([]); setCropCurrentIndex(0); setCropCurrentUrl(null);
    cropIntentRef.current = 'newPost';
  };

  const removeImage = (index) => { setNewPostImages(prev => prev.filter((_, i) => i !== index)); };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim() && newPostImages.length === 0) { setError(t('feed.pleaseAddContentError')); return; }
    const token = localStorage.getItem('token');
    if (!token) { setError(t('feed.pleaseLogInToPost')); return; }
    try {
      const formData = new FormData();
      formData.append('content', newPostContent);
      newPostImages.forEach((image) => { if (typeof image === 'object' && image instanceof File) formData.append('images', image); });
      const response = await fetch(`${API_BASE_URL}/posts`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (!response.ok) { const errorText = await response.text(); throw new Error(errorText || `Server error: ${response.status}`); }
      else {
        const data = await response.json();
        if (data.post) { const normalizedPost = normalizePost(data.post); setPosts(prev => [normalizedPost, ...prev].filter(p => p)); setSuccessMessage(t('feed.postCreatedToast')); }
      }
      setNewPostContent(''); setNewPostImages([]); setError(null);
      const fileInput = document.getElementById('media-upload');
      if (fileInput) fileInput.value = '';
    } catch (error) {
      if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) setError(t('feed.cannotConnectError'));
      else setError(error.message || t('feed.failedToCreatePost'));
    }
  };

  const navigateToProfile = (uid) => { if (uid) navigate(`/profile/${uid}`); };
  const handleImageClick = (images, startIndex = 0) => { if (images && images.length > 1) { setCurrentImages(images); setCurrentLightboxIndex(startIndex); setShowMultiImageLightbox(true); } };

  const normalizePost = (post) => {
    if (!post) return null;
    const author = post.author || post.user || {};
    const likes = post.likes || post.likedBy || post.likeCount || [];
    const saves = post.saves || post.savedBy || post.saveCount || [];
    const comments = post.comments || post.commentsList || [];
    return {
      _id: post._id || post.id,
      content: post.content || '',
      images: post.images || (post.image ? [post.image] : []),
      createdAt: post.createdAt || post.date || new Date().toISOString(),
      author: { _id: author._id || author.id || 'unknown', organizationName: author.organizationName || author.name || 'Unknown User', name: author.name || author.username || 'Unknown', username: author.username || '', profileImage: author.profileImage || '/nkarner/20260110_1932_Image Generation_remix_01kem8h20rewa81f8h55ncqmm9 (2).png', userType: author.userType || 'player', isVerified: author.isVerified || false, isOnline: author.isOnline || false },
      likes: Array.isArray(likes) ? likes : [],
      saves: Array.isArray(saves) ? saves : [],
      comments: Array.isArray(comments) ? comments : []
    };
  };

  useEffect(() => {
    const handleExternalPostCreated = (event) => {
      const normalizedPost = normalizePost(event?.detail?.post);
      if (!normalizedPost?._id) return;
      setPosts((prevPosts) => {
        const withoutDuplicate = prevPosts.filter((post) => post?._id !== normalizedPost._id);
        return [normalizedPost, ...withoutDuplicate];
      });
    };

    window.addEventListener('postCreated', handleExternalPostCreated);
    return () => window.removeEventListener('postCreated', handleExternalPostCreated);
  }, []);

  const buildCommentTree = (comments) => {
    if (!comments || !Array.isArray(comments)) return [];
    const commentMap = {}, rootComments = [];
    comments.forEach(comment => { if (comment && comment._id) commentMap[comment._id] = { ...comment, children: [] }; });
    comments.forEach(comment => { if (comment && comment._id) { if (comment.parentCommentId && commentMap[comment.parentCommentId]) commentMap[comment.parentCommentId].children.push(commentMap[comment._id]); else rootComments.push(commentMap[comment._id]); } });
    return rootComments;
  };

  const renderCommentTree = (comments, postId, depth = 0) => {
    if (!comments || comments.length === 0) return null;
    return comments.map(comment => (
      <React.Fragment key={`comment-${comment._id}`}>
        <CommentItem comment={comment} depth={depth} onLike={handleCommentLike} onReply={(commentId, mentionText) => { setReplyInputs({ ...replyInputs, [commentId]: mentionText || '' }); }} onReport={(commentId) => { setReportTarget({ type: 'comment', id: commentId }); setShowReportModal(true); }} onDelete={handleDeleteComment} onDeleteClick={handleDeleteCommentClick} currentUserId={userId} postId={postId} getImageUrl={getImageUrl} formatDate={formatDate} replyInputs={replyInputs} setReplyInputs={setReplyInputs} handleCommentReply={handleCommentReply} activeEmojiComment={activeEmojiComment} setActiveEmojiComment={setActiveEmojiComment} />
        {comment.children && comment.children.length > 0 && <div className="comment-children">{renderCommentTree(comment.children, postId, depth + 1)}</div>}
      </React.Fragment>
    ));
  };

  useEffect(() => { if (user && userId) { loadFeed(); loadSuggestedUsers(); loadFollowing(); loadFollowers(); loadFollowRequests(); loadRecommendedPrograms(); } }, [user, userId]);
  useEffect(() => { if (successMessage) { const timer = setTimeout(() => setSuccessMessage(null), 3000); return () => clearTimeout(timer); } }, [successMessage]);
  useEffect(() => { return () => { newPostImages.forEach(image => { if (image && typeof image === 'object' && image.type?.startsWith('image/')) { try { if (image.preview) URL.revokeObjectURL(image.preview); } catch (e) {} } }); }; }, [newPostImages]);

  useEffect(() => {
    if (user?.coverImage) {
      const coverUrl = getCoverImageUrl(user.coverImage, true);
      if (coverUrl) { const img = new Image(); img.src = coverUrl; img.onload = () => setCoverImageLoaded(true); img.onerror = () => setCoverImageLoaded(true); }
      else setCoverImageLoaded(true);
    } else setCoverImageLoaded(true);
  }, [user?.coverImage, user?._id, getCoverImageUrl]);

  useEffect(() => {
    if (suggestedUsers.length > 0) {
      const ignoredRecommendations = JSON.parse(localStorage.getItem('ignoredRecommendations') || '[]');
      const filtered = suggestedUsers.filter(suggestedUser => {
        const uid = getSafeUserId(suggestedUser.user || suggestedUser);
        const isAlreadyFollowing = following.some(f => getSafeUserId(f.user || f) === uid);
        return !isAlreadyFollowing && !ignoredRecommendations.includes(uid);
      });
      if (filtered.length !== suggestedUsers.length) setSuggestedUsers(filtered);
    }
  }, [suggestedUsers, following]);

  useEffect(() => {
    const loadUserPostsCount = async () => {
      if (!userId) return;
      try {
        const userData = await apiRequest('/user/posts?limit=1');
        setUserPostsCount(userData.pagination?.total ?? userData.user?.postsCount ?? userData.user?.postCount ?? userData.user?.totalPosts ?? userData.postsCount ?? userData.postCount ?? 0);
      } catch {
        setUserPostsCount(posts.filter(post => post && post.author && getSafeUserId(post.author) === userId).length);
      }
    };
    if (userId) loadUserPostsCount();
  }, [userId, posts]);

  const handleLogout = () => {
    if (window.confirm(t('settings.confirmLogout'))) {
      logout().then(() => navigate('/registration')).catch(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/registration');
      });
    }
  };

  const safePosts = posts.filter(post => post && post._id && post.author && post.author._id && typeof post.author === 'object');

  if (!user) {
    return (<div className="feed-container"><div className="feed-loading"><div className="loading-spinner"></div><p>Please log in to view the feed</p></div></div>);
  }

  if (loading && posts.length === 0) {
    return (<div className="feed-container"><div className="feed-loading"><div className="loading-spinner"></div><p>Loading feed...</p></div></div>);
  }

  // ==================== MOBILE LAYOUT ====================

  if (isMobile) {
    return (
      <div className="feed-container feed-container--mobile" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', paddingBottom: '70px' }}>

        {/* 1. Profile card */}
        <div className="user-profile-card" style={{ borderRadius: 0, marginBottom: 0 }}>
          <div className="cover-photo" style={{
            backgroundImage: user?.coverImage ? `url(${getCoverImageUrl(user.coverImage)})` : 'linear-gradient(135deg, var(--green-700) 0%, #4a9c7c 100%)',
            backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
            backgroundColor: 'var(--green-700)', minHeight: '140px', height: '140px',
            position: 'relative', display: 'block', borderRadius: 0,
          }}>
            {user?.coverImage && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.1)' }}></div>}
            <div className="profile-avatar-large">
              <img src={getImageUrl(user?.profileImage)} alt={user?.name} onError={handleImageError} />
            </div>
          </div>
          <div className="profile-info">
            {/* Stats moved down slightly */}
            <div className="profile-stats-horizontal" style={{ marginBottom: '12px' }}>
              <div className="profile-stat" onClick={() => { if (userId) { setFollowersModalUserId(userId); setFollowersModalType('followers'); setShowFollowersModal(true); } }} style={{ cursor: 'pointer' }}>
                <span className="stat-number">{followers.length}</span><span className="stat-label">{t("userPublicProfile.followersLabel")}</span>
              </div>
              <div className="profile-stat" onClick={() => { if (userId) { setFollowersModalUserId(userId); setFollowersModalType('following'); setShowFollowersModal(true); } }} style={{ cursor: 'pointer' }}>
                <span className="stat-number">{following.length}</span><span className="stat-label">{t("userPublicProfile.followingLabel")}</span>
              </div>
              <div className="profile-stat" onClick={() => userId && navigate(`/profile/${userId}`)} style={{ cursor: 'pointer' }}>
                <span className="stat-number">{userPostsCount}</span><span className="stat-label">{t("userPublicProfile.postsLabel")}</span>
              </div>
            </div>
            {/* Actions */}
            <div className="profile-actions-compact" style={{ marginBottom: '8px' }}>
              <button className="profile-action-btn profile-edit-btn clay-press" onClick={() => navigate('/dashboard')} title="Edit Profile">
                <Edit2 size={18} />
                <span className="icon-label" style={{ display: 'none' }}>Edit Profile</span>
              </button>
              <button className="profile-action-btn views-btn clay-press" onClick={() => navigate('/settings', { state: { tab: 'pro' } })} title="Who viewed your profile">
                <Eye size={18} />
                <span className="icon-label" style={{ display: 'none' }}>Who Viewed</span>
              </button>
              <button className="profile-action-btn profile-share-btn clay-press" onClick={(e) => {
                e.preventDefault(); e.stopPropagation();
                if (!user?._id) return;
                const profileUrl = `${window.location.origin}/profile/${user._id}`;
                const copyToClipboard = (text) => {
                  if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
                  const textArea = document.createElement('textarea');
                  textArea.value = text;
                  const container = document.createElement('div');
                  container.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
                  container.appendChild(textArea); document.body.appendChild(container);
                  textArea.focus(); textArea.select();
                  try { document.execCommand('copy'); } catch (err) { prompt('Copy this link:', text); }
                  finally { document.body.removeChild(container); }
                };
                copyToClipboard(profileUrl);
                setLinkCopiedNotification(true);
                setTimeout(() => setLinkCopiedNotification(false), 2500);
              }} title="Copy Profile Link">
                <Link size={18} />
                <span className="icon-label" style={{ display: 'none' }}>Copy Link</span>
              </button>
            </div>
            {/* Profile name at the bottom */}
            <h2 className="profile-namee">
              {user?.organizationName || user?.name || user?.username}
              {user?.userType === 'organizer' && user?.isVerified && <span className="verified-badge-profile">✓</span>}
            </h2>
            <p className="profile-username">@{user?.username || user?.email?.split('@')[0]}</p>
          </div>
        </div>

        {/* 2. Recommended strip */}
        <MobileRecommendedStrip
          suggestedUsers={suggestedUsers}
          following={following}
          isLoadingFollow={isLoadingFollow}
          getImageUrl={getImageUrl}
          navigate={navigate}
          sendFollowRequest={sendFollowRequest}
          setSuggestedUsers={setSuggestedUsers}
        />

        {/* 3. Create post */}
        <div style={{ padding: '10px 10px 0' }}>
          <div className="create-post" style={{ borderRadius: '14px' }}>
            <img src={getImageUrl(user?.profileImage)} alt="User" className="user-avatar" onClick={() => navigateToProfile(userId)} style={{ cursor: 'pointer' }} onError={handleImageError} />
            <form onSubmit={handlePostSubmit} className="post-form">
              <div className="post-input-container">
                <textarea
                  placeholder={t("feed.whatsOnMind")}
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  rows="3"
                  className="post-textarea"
                />
                {newPostImages.length > 0 && (
                  <div className="post-images-preview">
                    {newPostImages.map((image, index) => {
                      let imageUrl = typeof image === 'string' ? image : URL.createObjectURL(image);
                      return (
                        <div key={`image-preview-${index}`} className="post-image-preview" style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={imageUrl} alt={`Preview ${index + 1}`} onLoad={() => { if (typeof image !== 'string' && image instanceof Blob) setTimeout(() => URL.revokeObjectURL(imageUrl), 100); }} />
                          <div style={{ position: 'absolute', top: 4, right: 4 }}>
                            <XBtn onClick={() => removeImage(index)} label="Remove" variant="danger" size="sm" style={{ borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }} />
                          </div>
                          {index === 0 && newPostImages.length > 1 && <div className="image-counter-preview">+{newPostImages.length - 1}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="post-form-actions">
                <div className="media-actions">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="media-upload-input" id="media-upload" multiple />
                  <label htmlFor="media-upload" className="media-action-btn clay-press"><Camera size={18} />{t("feed.photos")}</label>
                </div>
                <button type="submit" className="post-button clay-press clay-focus-glow" disabled={!newPostContent.trim() && newPostImages.length === 0}>{t("feed.postBtn")}</button>
              </div>
            </form>
          </div>
        </div>

        {/* Messages */}
        {error && <div className="error-message" style={{ margin: '6px 10px 0' }}>{localizeMessage(error)}</div>}
        {successMessage && <div className="success-message" style={{ margin: '6px 10px 0' }}>{successMessage}</div>}

        {/* 4. Feed posts */}
        <div style={{ padding: '10px 10px 0', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {loading && posts.length === 0 ? (
            <div className="loading-spinner"></div>
          ) : safePosts.length === 0 ? (
            <div className="no-posts"><Shield size={48} style={{ color: '#ccc', marginBottom: '16px' }} /><p>{error || 'No posts yet. Be the first to post!'}</p></div>
          ) : (
            safePosts.map(post => {
              const commentTree = buildCommentTree(post.comments);
              return (
                <div key={`post-${post._id}`} className="post-card clay-lift" style={{ borderRadius: '14px', margin: 0 }}>
                  <div className="post-header">
                    <img src={getImageUrl(post.author?.profileImage)} alt={post.author?.name} className="post-author-avatar" onClick={(e) => { e.stopPropagation(); if (post.author?._id) navigate(`/profile/${post.author._id}`); }} style={{ cursor: 'pointer' }} onError={handleImageError} />
                    <div className="post-author-info" onClick={() => { if (post.author?._id) navigate(`/profile/${post.author._id}`); }} style={{ cursor: 'pointer' }}>
                      <div className="post-author-name">{post.author?.organizationName || post.author?.name || 'Unknown User'}</div>
                      <div className="post-meta"><span className="post-time">{formatDate(post.createdAt)}</span>{post.isEdited && <span className="post-edited-tag" style={{ fontSize: '12px', color: 'var(--ink-400)', marginLeft: '4px' }}> · edited</span>}</div>
                    </div>
                    <PostOptions post={post} currentUserId={userId} currentUser={user}
                      onDelete={(postId) => { setPostToDelete(postId); setPostObjToDelete(post); setShowDeleteModal(true); }}
                      onReport={(postId) => { setReportTarget({ type: 'post', id: postId }); setShowReportModal(true); }}
                      onEdit={handleStartEditPost}
                      onArchive={handleArchivePost}
                    />
                  </div>
                  <div className="post-content" style={{ overflow: 'visible' }}>
                    {post.content && <ExpandableText text={post.content} />}
                  </div>
                  {post.images && post.images.length > 0 && (
                    <div className="post-images-carousel" style={{ marginTop: post.content ? '12px' : '0' }}>
                      <ImageCarousel images={post.images} getImageUrl={getImageUrl} onImageClick={(index) => handleImageClick(post.images, index)} />
                    </div>
                  )}
                  {/* Post actions in a single row */}
                  <div className="post-actions" style={{ marginTop: '14px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                    <button className={`action-button like-button ${isLiked(post) ? 'liked' : ''}`} onClick={() => handleLike(post._id)} disabled={isLiking[post._id]} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                      <div className="action-button-content">
                        {isLiking[post._id] ? <div className="mini-spinner"></div> : <Heart size={20} fill={isLiked(post) ? 'var(--fg-gold-bright, #ffd873)' : 'none'} stroke="var(--fg-gold-bright, #ffd873)" />}
                        <span className="action-count">{post.likes?.length || 0}</span>
                      </div>
                    </button>
                    <button className="action-button comment-button" onClick={() => toggleComments(post._id)} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                      <div className="action-button-content"><MessageCircle size={20} stroke="var(--fg-gold-bright, #ffd873)" /><span className="action-count">{post.comments?.length || 0}</span></div>
                    </button>
                    <button className={`action-button save-button ${isPostSaved(post) ? 'saved' : ''}`} onClick={() => handleSavePost(post._id)} disabled={isSaving[post._id]} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                      <div className="action-button-content">
                        {isSaving[post._id] ? <div className="mini-spinner"></div> : <Bookmark size={20} fill={isPostSaved(post) ? 'var(--fg-gold-bright, #ffd873)' : 'none'} stroke="var(--fg-gold-bright, #ffd873)" />}
                        <span className="action-count">{post.saves?.length || 0}</span>
                      </div>
                    </button>
                    <button className="action-button send-button" onClick={() => handleSendPostButtonClick(post._id)} style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                      <div className="action-button-content">
                        <img src="/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png" alt="Share" style={{ width: '20px', height: '20px', transform: 'rotate(30deg)' }} />
                        <span className="action-count">Share</span>
                      </div>
                    </button>
                  </div>
                  {showComments[post._id] && (
                    <div className="post-comments-section">
                      <div className="comments-list">
                        {commentTree.length === 0 ? (
                          <div className="no-comments"><MessageCircle size={32} style={{ color: '#ccc' }} /><p>No comments yet</p><small>Be the first to comment!</small></div>
                        ) : renderCommentTree(commentTree, post._id)}
                      </div>
                      <div className="add-comment">
                        <div className="comment-input-wrapper" style={{ position: 'relative', flex: 1 }}>
                          <input type="text" placeholder="Write a comment..." value={commentInputs[post._id] || ''} onChange={(e) => setCommentInputs({ ...commentInputs, [post._id]: e.target.value })} onKeyPress={(e) => e.key === 'Enter' && handleComment(post._id)} className="comment-input" style={{ width: '100%', padding: '8px 12px', paddingRight: '36px', border: '1px solid #e0e0e0', borderRadius: '20px' }} />
                          <button className="emoji-toggle-btn" onClick={() => setActiveEmojiComment(activeEmojiComment === post._id ? null : post._id)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--ink-400)' }}><Smile size={16} /></button>
                          {activeEmojiComment === post._id && (
                            <div style={{ position: 'absolute', bottom: '100%', left: '0', zIndex: 1000 }}>
                              <EmojiPicker onEmojiClick={(emojiData) => { setCommentInputs({ ...commentInputs, [post._id]: (commentInputs[post._id] || '') + emojiData.emoji }); setActiveEmojiComment(null); }} disableAutoFocus={true} native={true} height={300} width={280} />
                            </div>
                          )}
                        </div>
                        <button className="comment-submit-btn" onClick={() => handleComment(post._id)} disabled={!commentInputs[post._id]?.trim() || isCommenting[post._id]} style={{ background: 'var(--green-700)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: commentInputs[post._id]?.trim() && !isCommenting[post._id] ? 1 : 0.5 }}>
                          {isCommenting[post._id] ? <div className="mini-spinner"></div> : <Send size={16} color="white" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Modals */}
        <ModerationWarningModal isOpen={showModerationWarning} onClose={() => setShowModerationWarning(false)} reason={moderationReason} />
        <ImageCropModal isOpen={showCropModal} imageFile={cropPendingFiles[cropCurrentIndex] || null} imageUrl={cropCurrentUrl} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />
        <EditPostModal isOpen={!!editingPost} post={editingPost} onClose={handleCancelEditPost} onSave={handleUpdatePost} getImageUrl={getImageUrl} isSaving={isUpdatingPost} newImages={editPostNewImages} onRemoveNewImage={removeEditPostNewImage} onRequestCrop={beginEditPostImageCrop} onClearNewImages={() => setEditPostNewImages([])} />
        <ShareModal isOpen={showUserSelector} onClose={() => { setShowUserSelector(false); setCurrentPostForSharing(null); }} post={currentPostForSharing} following={following} followers={followers} currentUserId={userId} onSelectUser={sendPostAsMessage} isSending={isSendingPost} onLinkCopied={() => { setLinkCopiedNotification(true); setTimeout(() => setLinkCopiedNotification(false), 2500); }} />
        <DeletePostModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setPostToDelete(null); setPostObjToDelete(null); }} onDelete={handleDeletePost} postId={postToDelete} post={postObjToDelete} currentUser={user} />
        <DeleteCommentModal isOpen={showDeleteCommentModal} onClose={() => { setShowDeleteCommentModal(false); setCommentToDelete(null); setPostIdForCommentDelete(null); }} onDelete={(commentId) => handleDeleteComment(commentId, postIdForCommentDelete)} commentId={commentToDelete} />
        <ReportModal isOpen={showReportModal} onClose={() => { setShowReportModal(false); setReportTarget({ type: '', id: '' }); }} onReport={handleReport} type={reportTarget.type} targetId={reportTarget.id} />
        <MultiImageLightbox images={currentImages} isOpen={showMultiImageLightbox} onClose={() => { setShowMultiImageLightbox(false); setCurrentImages([]); }} getImageUrl={getImageUrl} />
        <FollowersFollowingModal isOpen={showFollowersModal} onClose={() => { setShowFollowersModal(false); setFollowersModalUserId(null); }} userId={followersModalUserId} type={followersModalType} getImageUrl={getImageUrl} navigate={navigate} />
      </div>
    );
  }

  // ==================== DESKTOP / TABLET LAYOUT ====================

  return (
    <div className="feed-container">
      {/* Left Panel - Updated Alignment */}
      <div className="feed-left-panel" style={isTablet ? {
        width: '72px', minWidth: '72px', padding: '16px 0',
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
        overflow: 'hidden',
      } : {
        alignItems: 'flex-start',
        padding: '16px 16px 0 16px',
      }}>
        <div className="brand-section" style={isTablet ? { justifyContent: 'flex-start', padding: '0 0 16px 12px', width: '100%' } : { marginBottom: '24px', paddingLeft: '4px' }}>
          <h1 style={isTablet ? { fontSize: 0, width: 32, overflow: 'hidden', display: 'flex', alignItems: 'center' } : { fontSize: '24px', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png" alt="Greenwich" style={{ width: '24px', height: '24px' }} />
            {!isTablet && 'Greenwich'}
          </h1>
        </div>
        <div className="nav-menu" style={isTablet ? { width: '100%' } : { display: 'flex', flexDirection: 'column', width: '100%', gap: '8px', alignItems: 'flex-start' }}>
          {/* Each button now explicitly uses flex row to place icon and text side by side */}
          <button className="feed-nav-item active" style={isTablet ? { justifyContent: 'flex-start', padding: '10px 12px', borderRadius: '12px', width: '100%', margin: '0', display: 'flex', alignItems: 'center', gap: '12px', flexDirection: 'row' } : { justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '12px', width: '100%', gap: '12px', display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            <Home size={20} />{!isTablet && <span>{t("navbar.feed")}</span>}
          </button>
          <button className="feed-nav-item" onClick={() => navigate('/search')} style={isTablet ? { justifyContent: 'flex-start', padding: '10px 12px', borderRadius: '12px', width: '100%', margin: '0', display: 'flex', alignItems: 'center', gap: '12px', flexDirection: 'row' } : { justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '12px', width: '100%', gap: '12px', display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            <Search size={20} />{!isTablet && <span>{t("feed.sidebarExplore")}</span>}
          </button>
          <button className="feed-nav-item" onClick={() => navigate('/messages')} style={isTablet ? { justifyContent: 'flex-start', padding: '10px 12px', borderRadius: '12px', width: '100%', margin: '0', display: 'flex', alignItems: 'center', gap: '12px', flexDirection: 'row' } : { justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '12px', width: '100%', gap: '12px', display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            <MessageCircle size={20} />{!isTablet && <span>{t("navbar.messages")}</span>}
          </button>
          <button className="feed-nav-item" onClick={() => navigate('/settings')} style={isTablet ? { justifyContent: 'flex-start', padding: '10px 12px', borderRadius: '12px', width: '100%', margin: '0', display: 'flex', alignItems: 'center', gap: '12px', flexDirection: 'row' } : { justifyContent: 'flex-start', padding: '10px 16px', borderRadius: '12px', width: '100%', gap: '12px', display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
            <Settings size={20} />{!isTablet && <span>{t("navbar.settings")}</span>}
          </button>
        </div>
        {!isTablet && (
          <div className="following-section" style={{ width: '100%', marginTop: '24px' }}>
            <div className="following-header"><Users size={18} /><h3>{t("userPublicProfile.followingLabel")}</h3></div>
            <div className="following-list">
              {following.length === 0 ? (
                <div className="no-following"><p>{t("feed.noFollowingYet")}</p><small>{t("feed.followPeopleHint")}</small></div>
              ) : (
                following.slice(0, 6).map((followingItem, index) => {
                  const userData = followingItem.user || followingItem.following || followingItem;
                  const uid = getSafeUserId(userData);
                  const userName = getSafeUserName(userData);
                  const isOnline = userData.isOnline || false;
                  return (
                    <div key={`following-${uid || index}`} className="following-user">
                      <div className="following-avatar-container" onClick={() => uid && navigateToProfile(uid)} style={{ cursor: uid ? 'pointer' : 'default' }}>
                        <img src={getImageUrl(userData.profileImage)} alt={userName} className="following-user-avatar" onError={handleImageError} />
                      </div>
                      <span className="following-user-name" title={userName} onClick={() => uid && navigateToProfile(uid)} style={{ cursor: uid ? 'pointer' : 'default' }}>
                        {userName.length > 12 ? userName.substring(0, 12) + '...' : userName}
                      </span>
                      <div className={`following-status ${isOnline ? 'online' : 'offline'}`}></div>
                    </div>
                  );
                })
              )}
            </div>
            {following.length > 6 && <button className="view-all-following" onClick={() => navigate('/dashboard', { state: { section: 'about', openFollowingModal: true } })}>{t("feed.viewAllFollowing", { count: following.length })}</button>}
          </div>
        )}
        <button className="logout-button" onClick={handleLogout} style={isTablet ? { width: '44px', height: '44px', justifyContent: 'center', padding: 0, borderRadius: '50%', marginTop: 'auto', alignSelf: 'flex-start', marginLeft: '12px', display: 'flex', alignItems: 'center' } : { marginTop: 'auto', width: '100%', justifyContent: 'flex-start', padding: '10px 16px', gap: '12px', display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <LogOut size={20} />{!isTablet && <span>{t("common.logout")}</span>}
        </button>
      </div>

      {/* Middle Panel */}
      <div className="feed-main">
        <div className="user-profile-card">
          <div className="cover-photo" style={{ backgroundImage: user?.coverImage ? `url(${getCoverImageUrl(user.coverImage)})` : 'linear-gradient(135deg, var(--green-700) 0%, #4a9c7c 100%)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundColor: 'var(--green-700)', minHeight: '200px', height: '200px', position: 'relative', display: 'block' }}>
            {user?.coverImage && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '8px 8px 0 0' }}></div>}
            <div className="profile-avatar-large">
              <img src={getImageUrl(user?.profileImage)} alt={user?.name} onError={handleImageError} />
            </div>
          </div>
          <div className="profile-info">
            <div className="profile-header-row">
              <div className="profile-stats-horizontal">
                <div className="profile-stat" onClick={() => { if (userId) { setFollowersModalUserId(userId); setFollowersModalType('followers'); setShowFollowersModal(true); } }} style={{ cursor: userId ? 'pointer' : 'default' }}>
                  <span className="stat-number">{followers.length}</span><span className="stat-label">{t("userPublicProfile.followersLabel")}</span>
                </div>
                <div className="profile-stat" onClick={() => { if (userId) { setFollowersModalUserId(userId); setFollowersModalType('following'); setShowFollowersModal(true); } }} style={{ cursor: userId ? 'pointer' : 'default' }}>
                  <span className="stat-number">{following.length}</span><span className="stat-label">{t("userPublicProfile.followingLabel")}</span>
                </div>
                <div className="profile-stat" onClick={() => userId && navigate(`/profile/${userId}`)} style={{ cursor: userId ? 'pointer' : 'default' }}>
                  <span className="stat-number">{userPostsCount}</span><span className="stat-label">{t("userPublicProfile.postsLabel")}</span>
                </div>
              </div>
              <div className="profile-actions-compact">
                <button className="profile-action-btn profile-edit-btn clay-press" onClick={() => navigate('/dashboard')} title="Edit Profile">
                  <Edit2 size={18} />
                  <span className="icon-label" style={{ display: 'none' }}>Edit Profile</span>
                </button>
                <button className="profile-action-btn views-btn clay-press" onClick={() => navigate('/settings', { state: { tab: 'pro' } })} title="Who viewed your profile">
                  <Eye size={18} />
                  <span className="icon-label" style={{ display: 'none' }}>Who Viewed</span>
                </button>
                <button className="profile-action-btn profile-share-btn clay-press" onClick={(e) => {
                  e.preventDefault(); e.stopPropagation();
                  if (!user?._id) { setError(t('feed.cannotCopyProfileLinkError')); return; }
                  const profileUrl = `${window.location.origin}/profile/${user._id}`;
                  const copyToClipboard = (text) => {
                    if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(text);
                    const textArea = document.createElement('textarea');
                    textArea.value = text;
                    const container = document.createElement('div');
                    container.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
                    container.appendChild(textArea); document.body.appendChild(container);
                    textArea.focus(); textArea.select();
                    try { document.execCommand('copy'); } catch (err) { prompt('Copy this link:', text); }
                    finally { document.body.removeChild(container); }
                  };
                  copyToClipboard(profileUrl);
                  setLinkCopiedNotification(true);
                  setTimeout(() => setLinkCopiedNotification(false), 2500);
                }} title="Copy Profile Link">
                  <Link size={18} />
                  <span className="icon-label" style={{ display: 'none' }}>Copy Link</span>
                </button>
              </div>
            </div>
            <h2 className="profile-namee">
              {user?.organizationName || user?.name || user?.username}
              {user?.userType === 'organizer' && user?.isVerified && <span className="verified-badge-profile">✓</span>}
            </h2>
            <p className="profile-username">@{user?.username || user?.email?.split('@')[0]}</p>
          </div>
        </div>

        {/* Create Post */}
        <div className="create-post">
          <img src={getImageUrl(user?.profileImage)} alt="User" className="user-avatar" onClick={() => navigateToProfile(userId)} style={{ cursor: 'pointer' }} onError={handleImageError} />
          <form onSubmit={handlePostSubmit} className="post-form">
            <div className="post-input-container">
              <textarea placeholder={t("feed.whatsOnMind")} value={newPostContent} onChange={(e) => setNewPostContent(e.target.value)} rows="3" className="post-textarea" />
              {newPostImages.length > 0 && (
                <div className="post-images-preview">
                  {newPostImages.map((image, index) => {
                    let imageUrl = typeof image === 'string' ? image : URL.createObjectURL(image);
                    return (
                      <div key={`image-preview-${index}`} className="post-image-preview" style={{ position: 'relative', display: 'inline-block' }}>
                        <img src={imageUrl} alt={`Preview ${index + 1}`} onLoad={() => { if (typeof image !== 'string' && image instanceof Blob) setTimeout(() => URL.revokeObjectURL(imageUrl), 100); }} />
                        <div style={{ position: 'absolute', top: 4, right: 4 }}>
                          <XBtn onClick={() => removeImage(index)} label="Remove" variant="danger" size="sm" style={{ borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.25)' }} />
                        </div>
                        {index === 0 && newPostImages.length > 1 && <div className="image-counter-preview">+{newPostImages.length - 1}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="post-form-actions">
              <div className="media-actions">
                <input type="file" accept="image/*" onChange={handleImageUpload} className="media-upload-input" id="media-upload" multiple />
                <label htmlFor="media-upload" className="media-action-btn clay-press"><Camera size={18} />{t("feed.photos")}</label>
              </div>
              <button type="submit" className="post-button clay-press clay-focus-glow" disabled={(!newPostContent.trim() && newPostImages.length === 0)}>
                Post
              </button>
            </div>
          </form>
        </div>

        {error && <div className="error-message">{localizeMessage(error)}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        {/* Feed Posts */}
        <div className="feed-posts">
          {loading && posts.length === 0 ? (
            <div className="loading-spinner"></div>
          ) : safePosts.length === 0 ? (
            <div className="no-posts"><Shield size={48} style={{ color: '#ccc', marginBottom: '16px' }} /><p>{error || 'No posts yet. Be the first to post!'}</p></div>
          ) : (
            safePosts.map(post => {
              const commentTree = buildCommentTree(post.comments);
              return (
                <div key={`post-${post._id || `temp-${Date.now()}`}`} className="post-card clay-lift">
                  <div className="post-header">
                    <img src={getImageUrl(post.author?.profileImage)} alt={post.author?.name} className="post-author-avatar" onClick={(e) => { e.stopPropagation(); if (post.author?._id) navigate(`/profile/${post.author._id}`); }} style={{ cursor: 'pointer' }} onError={handleImageError} />
                    <div className="post-author-info" onClick={() => { if (post.author?._id) navigate(`/profile/${post.author._id}`); }} style={{ cursor: 'pointer' }}>
                      <div className="post-author-name">{post.author?.organizationName || post.author?.name || 'Unknown User'}</div>
                      <div className="post-meta"><span className="post-time">{formatDate(post.createdAt)}</span>{post.isEdited && <span className="post-edited-tag" style={{ fontSize: '12px', color: 'var(--ink-400)', marginLeft: '4px' }}> · edited</span>}</div>
                    </div>
                    <PostOptions
                      post={post}
                      currentUserId={userId}
                      currentUser={user}
                      onDelete={(postId) => {
                        setPostToDelete(postId);
                        setPostObjToDelete(post);
                        setShowDeleteModal(true);
                      }}
                      onReport={(postId) => { setReportTarget({ type: 'post', id: postId }); setShowReportModal(true); }}
                      onEdit={handleStartEditPost}
                      onArchive={handleArchivePost}
                    />
                  </div>

                  <div className="post-content" style={{ overflow: 'visible' }}>
                    {post.content && <ExpandableText text={post.content} />}
                  </div>
                  {post.images && post.images.length > 0 && (
                    <div className="post-images-carousel" style={{ marginTop: post.content ? '12px' : '0', display: 'block', width: '100%', overflow: 'visible', position: 'relative' }}>
                      <ImageCarousel images={post.images} getImageUrl={getImageUrl} onImageClick={(index) => handleImageClick(post.images, index)} />
                    </div>
                  )}

                  <div className="post-actions" style={{ marginTop: '14px' }}>
                    <button className={`action-button like-button ${isLiked(post) ? 'liked' : ''}`} onClick={() => handleLike(post._id)} disabled={isLiking[post._id]}>
                      <div className="action-button-content">
                        {isLiking[post._id] ? <div className="mini-spinner"></div> : <Heart size={20} fill={isLiked(post) ? 'var(--fg-gold-bright, #ffd873)' : 'none'} stroke="var(--fg-gold-bright, #ffd873)" />}
                        <span className="action-count">{post.likes?.length || 0}</span>
                      </div>
                    </button>
                    <button className="action-button comment-button" onClick={() => toggleComments(post._id)}>
                      <div className="action-button-content"><MessageCircle size={20} stroke="var(--fg-gold-bright, #ffd873)" /><span className="action-count">{post.comments?.length || 0}</span></div>
                    </button>
                    <button className={`action-button save-button ${isPostSaved(post) ? 'saved' : ''}`} onClick={() => handleSavePost(post._id)} disabled={isSaving[post._id]}>
                      <div className="action-button-content">
                        {isSaving[post._id] ? <div className="mini-spinner"></div> : <Bookmark size={20} fill={isPostSaved(post) ? 'var(--fg-gold-bright, #ffd873)' : 'none'} stroke="var(--fg-gold-bright, #ffd873)" />}
                        <span className="action-count">{post.saves?.length || 0}</span>
                      </div>
                    </button>
                    <button className="action-button send-button" onClick={() => handleSendPostButtonClick(post._id)}>
                      <div className="action-button-content">
                        <img src="/nkarner/Screenshot_2025-09-08_200654-removebg-preview (1).png" alt="Share" style={{ width: '20px', height: '20px', transform: 'rotate(30deg)' }} />
                        <span className="action-count">Share</span>
                      </div>
                    </button>
                  </div>
                  {showComments[post._id] && (
                    <div className="post-comments-section">
                      <div className="comments-list">
                        {commentTree.length === 0 ? (
                          <div className="no-comments"><MessageCircle size={32} style={{ color: '#ccc' }} /><p>No comments yet</p><small>Be the first to comment!</small></div>
                        ) : renderCommentTree(commentTree, post._id)}
                      </div>
                      <div className="add-comment">
                        <div className="comment-input-wrapper" style={{ position: 'relative', flex: 1 }}>
                          <input type="text" placeholder="Write a comment..." value={commentInputs[post._id] || ''} onChange={(e) => setCommentInputs({ ...commentInputs, [post._id]: e.target.value })} onKeyPress={(e) => e.key === 'Enter' && handleComment(post._id)} className="comment-input" style={{ width: '100%', padding: '8px 12px', paddingRight: '36px', border: '1px solid #e0e0e0', borderRadius: '20px' }} />
                          <button className="emoji-toggle-btn" onClick={() => setActiveEmojiComment(activeEmojiComment === post._id ? null : post._id)} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--ink-400)' }}><Smile size={16} /></button>
                          {activeEmojiComment === post._id && (
                            <div style={{ position: 'absolute', bottom: '100%', left: '0', zIndex: 1000 }}>
                              <EmojiPicker onEmojiClick={(emojiData) => { setCommentInputs({ ...commentInputs, [post._id]: (commentInputs[post._id] || '') + emojiData.emoji }); setActiveEmojiComment(null); }} disableAutoFocus={true} native={true} height={350} width={300} />
                            </div>
                          )}
                        </div>
                        <button className="comment-submit-btn" onClick={() => handleComment(post._id)} disabled={!commentInputs[post._id]?.trim() || isCommenting[post._id]} style={{ background: 'var(--green-700)', color: 'white', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', opacity: commentInputs[post._id]?.trim() && !isCommenting[post._id] ? 1 : 0.5 }}>
                          {isCommenting[post._id] ? <div className="mini-spinner"></div> : <Send size={16} color="white" />}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Right Panel */}
      <div className="feed-right-panel">
        {recommendedPrograms.length > 0 && (
          <div className="recommended-section" style={{ marginBottom: '16px' }}>
            <div className="recommended-header">
              <h3>{t("feed.programsForYou")}</h3>
              <button className="see-all-btn" onClick={() => navigate('/programs')}>{t("feed.seeAll")}</button>
            </div>
            <div className="recommended-list">
              {recommendedPrograms.map((program) => (
                <div key={program._id} className="recommended-user" style={{ cursor: 'pointer' }} onClick={() => navigate(`/programs?program=${program._id}`)}>
                  <div className="recommended-info" style={{ marginLeft: 0 }}>
                    <div className="recommended-name">{(program.title || '').length > 32 ? program.title.slice(0, 32) + '...' : program.title}</div>
                    <div className="recommended-title">
                      {program.organizationName || (Array.isArray(program.categories) ? program.categories[0] : '') || t("feed.programsForYouFallback")}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {!recommendedProgramsPersonalized && (
              <p style={{ margin: '8px 12px 0', fontSize: '11px', color: '#8a8a8a' }}>{t("feed.programsForYouPopularHint")}</p>
            )}
          </div>
        )}

        <div className="recommended-section">
          <div className="recommended-header"><h3>{t("feed.recommendedForYou")}</h3><button className="see-all-btn" onClick={() => navigate('/search')}>{t("feed.seeAll")}</button></div>
          <div className="recommended-list">
            {suggestedUsers.length === 0 ? (
              <div className="no-recommendations">
                <Users size={32} style={{ color: '#ccc', marginBottom: '12px' }} />
                <p>{t("feed.noRecommendationsYet")}</p><small>{t("feed.recommendationsHint")}</small>
                <button className="explore-btn" onClick={() => navigate('/search')} style={{ marginTop: '12px' }}>{t("common.exploreUsers")}</button>
              </div>
            ) : (
              suggestedUsers.slice(0, 5).map((suggestedUser, index) => {
                const userData = suggestedUser.user || suggestedUser;
                const uid = getSafeUserId(userData);
                const userName = getSafeUserName(userData);
                const userType = userData.userType || 'player';
                const isFollowingUser = following.some(f => getSafeUserId(f.user || f) === uid);
                return (
                  <div key={`recommended-${uid || index}`} className="recommended-user">
                    <div className="recommended-avatar-container" onClick={() => navigate(`/profile/${uid}`)} style={{ cursor: 'pointer' }}>
                      <img src={getImageUrl(userData.profileImage)} alt={userName} className="recommended-avatar" onError={handleImageError} />
                      {userType === 'organizer' && userData.isVerified && <div className="verified-badge-tiny">✓</div>}
                    </div>
                    <div className="recommended-info" onClick={() => navigate(`/profile/${uid}`)} style={{ cursor: 'pointer' }}>
                      <div className="recommended-name">{userName.length > 15 ? userName.substring(0, 15) + '...' : userName}</div>
                      <div className="recommended-title">{userType === 'organizer' ? t("userPublicProfile.organization") : t("userPublicProfile.communityMember")}</div>
                    </div>
                    <button className={`follow-btn-small ${isFollowingUser ? 'following' : ''}`} onClick={async (e) => { e.stopPropagation(); if (!isFollowingUser && uid) { await sendFollowRequest(uid); setSuggestedUsers(prev => prev.filter(u => getSafeUserId(u.user || u) !== uid)); } }} disabled={isFollowingUser || isLoadingFollow[uid] || !uid} title={isFollowingUser ? t("feed.alreadyFollowing") : t("followButton.follow")}>
                      {isLoadingFollow[uid] ? <div className="mini-spinner" style={{ width: '12px', height: '12px' }}></div> : isFollowingUser ? <UserCheck size={14} /> : <UserPlus size={14} />}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <ModerationWarningModal isOpen={showModerationWarning} onClose={() => setShowModerationWarning(false)} reason={moderationReason} />
      <ImageCropModal isOpen={showCropModal} imageFile={cropPendingFiles[cropCurrentIndex] || null} imageUrl={cropCurrentUrl} onConfirm={handleCropConfirm} onCancel={handleCropCancel} />
      <EditPostModal isOpen={!!editingPost} post={editingPost} onClose={handleCancelEditPost} onSave={handleUpdatePost} getImageUrl={getImageUrl} isSaving={isUpdatingPost} newImages={editPostNewImages} onRemoveNewImage={removeEditPostNewImage} onRequestCrop={beginEditPostImageCrop} onClearNewImages={() => setEditPostNewImages([])} />
      <ShareModal isOpen={showUserSelector} onClose={() => { setShowUserSelector(false); setCurrentPostForSharing(null); }} post={currentPostForSharing} following={following} followers={followers} currentUserId={userId} onSelectUser={sendPostAsMessage} isSending={isSendingPost} onLinkCopied={() => { setLinkCopiedNotification(true); setTimeout(() => setLinkCopiedNotification(false), 2500); }} />

      {linkCopiedNotification && (
        <div className="link-copied-toast" style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: 'var(--green-700)', color: 'white', padding: '12px 24px', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', zIndex: 10001, fontSize: '14px', fontWeight: '500' }}>Link copied</div>
      )}

      <DeletePostModal isOpen={showDeleteModal} onClose={() => { setShowDeleteModal(false); setPostToDelete(null); setPostObjToDelete(null); }} onDelete={handleDeletePost} postId={postToDelete} post={postObjToDelete} currentUser={user} />
      <DeleteCommentModal isOpen={showDeleteCommentModal} onClose={() => { setShowDeleteCommentModal(false); setCommentToDelete(null); setPostIdForCommentDelete(null); }} onDelete={(commentId) => handleDeleteComment(commentId, postIdForCommentDelete)} commentId={commentToDelete} />
      <ReportModal isOpen={showReportModal} onClose={() => { setShowReportModal(false); setReportTarget({ type: '', id: '' }); }} onReport={handleReport} type={reportTarget.type} targetId={reportTarget.id} />
      <MultiImageLightbox images={currentImages} isOpen={showMultiImageLightbox} onClose={() => { setShowMultiImageLightbox(false); setCurrentImages([]); }} getImageUrl={getImageUrl} />
      <FollowersFollowingModal isOpen={showFollowersModal} onClose={() => { setShowFollowersModal(false); setFollowersModalUserId(null); }} userId={followersModalUserId} type={followersModalType} getImageUrl={getImageUrl} navigate={navigate} />

    </div>
  );
}

export default Feed;
